import React, { useEffect, useState } from 'react';
import { adsAPI } from '../api/client';
import { Card, Badge, Button, NetworkIcon, EmptyState, ConfirmDialog } from '../components/ui';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import toast from 'react-hot-toast';

const STATUS_COLOR = { ACTIVE: '#10b981', PAUSED: '#f59e0b', DELETED: '#ef4444', ARCHIVED: '#6b7280' };
const STATUS_LABEL = { ACTIVE: '● Activa', PAUSED: '⏸ Pausada', DELETED: '✕ Eliminada', ARCHIVED: '📦 Archivada' };
const OBJECTIVES = [
  { id: 'AWARENESS',    label: '📣 Reconocimiento', desc: 'Dar a conocer tu marca' },
  { id: 'TRAFFIC',      label: '🌐 Tráfico',         desc: 'Llevar visitas a tu sitio' },
  { id: 'ENGAGEMENT',   label: '❤️ Interacción',      desc: 'Likes, comentarios, compartidos' },
  { id: 'LEADS',        label: '📋 Clientes potenciales', desc: 'Capturar datos de contacto' },
  { id: 'CONVERSIONS',  label: '💰 Conversiones',    desc: 'Ventas y acciones valiosas' },
  { id: 'VIDEO_VIEWS',  label: '▶️ Reproducciones',  desc: 'Máximas vistas de video' },
];
const COUNTRIES_ES = { CO: '🇨🇴 Colombia', MX: '🇲🇽 México', ES: '🇪🇸 España', AR: '🇦🇷 Argentina', US: '🇺🇸 Estados Unidos', CL: '🇨🇱 Chile', PE: '🇵🇪 Perú', VE: '🇻🇪 Venezuela' };
const NET_COLOR = { facebook: '#1877F2', instagram: '#E1306C', tiktok: '#69C9D0' };

function fmt(v) { return v > 999999 ? (v/1000000).toFixed(1)+'M' : v > 999 ? (v/1000).toFixed(1)+'K' : (v||0).toString(); }
function fmtCurrency(v, currency='USD') { return new Intl.NumberFormat('es', { style: 'currency', currency, minimumFractionDigits: 2 }).format(v||0); }

// ── Overview Cards ────────────────────────────────────────
function OverviewCards({ data }) {
  if (!data) return null;
  const cards = [
    { icon: '💰', label: 'Gasto total',     val: fmtCurrency(data.total_spend), color: '#10b981' },
    { icon: '👁',  label: 'Impresiones',    val: fmt(data.total_impressions),    color: '#7c3aed' },
    { icon: '🖱',  label: 'Clics',          val: fmt(data.total_clicks),         color: '#3b82f6' },
    { icon: '📊',  label: 'CTR promedio',   val: `${data.avg_ctr}%`,             color: '#f59e0b' },
    { icon: '💵',  label: 'CPM promedio',   val: fmtCurrency(data.avg_cpm),      color: '#06b6d4' },
    { icon: '🚀',  label: 'Campañas activas', val: data.active_campaigns,        color: '#10b981' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
      {cards.map(c => (
        <div key={c.label} style={{ padding: '16px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', borderLeft: `3px solid ${c.color}` }}>
          <div style={{ fontSize: '22px', marginBottom: '8px' }}>{c.icon}</div>
          <div style={{ fontWeight: '800', fontSize: '20px', color: c.color }}>{c.val}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{c.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── Network Spend Chart ───────────────────────────────────
function NetworkChart({ data }) {
  if (!data?.by_network) return null;
  const chartData = Object.entries(data.by_network).map(([net, d]) => ({
    name: net.charAt(0).toUpperCase() + net.slice(1),
    Gasto: d.spend, Impresiones: d.impressions / 1000,
    fill: NET_COLOR[net] || '#888',
  }));
  return (
    <Card>
      <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: '700', fontSize: '15px', marginBottom: '16px' }}>Distribución por red</h3>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={chartData}>
          <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 12 }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', fontSize: '12px' }} />
          <Bar dataKey="Gasto" radius={[4,4,0,0]}>
            {chartData.map((e, i) => <Cell key={i} fill={e.fill} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
}

// ── Create Campaign Form ──────────────────────────────────
function CreateCampaignForm({ accounts, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    ad_account_id: accounts[0]?.id || '',
    network: accounts[0]?.network || 'facebook',
    name: '', description: '',
    objective: 'TRAFFIC',
    budget_type: 'DAILY', budget_amount: 0,
    budget_level: 'CAMPAIGN',
    start_time: '', end_time: '',
  });
  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const availableNets = [...new Set(accounts.map(a => a.network))];

  return (
    <form onSubmit={e => { e.preventDefault(); onSave(form); }} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Red social */}
      <div>
        <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>Red social</label>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {availableNets.map(net => (
            <button type="button" key={net} onClick={() => {
              const acc = accounts.find(a => a.network === net);
              setForm(f => ({ ...f, network: net, ad_account_id: acc?.id || f.ad_account_id }));
            }} style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px',
              borderRadius: 'var(--radius-md)', border: `1px solid ${form.network === net ? NET_COLOR[net] : 'var(--border)'}`,
              background: form.network === net ? `${NET_COLOR[net]}20` : 'var(--bg-elevated)',
              cursor: 'pointer',
            }}>
              <NetworkIcon network={net} size={20} />
              <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', textTransform: 'capitalize' }}>{net}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Cuenta publicitaria */}
      <div>
        <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Cuenta publicitaria</label>
        <select value={form.ad_account_id} onChange={e => set('ad_account_id')(e.target.value)}
          style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '13px', fontFamily: 'var(--font-body)', outline: 'none' }}>
          {accounts.filter(a => a.network === form.network).map(a => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
        </select>
      </div>

      {/* Nombre */}
      <div>
        <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Nombre de la campaña</label>
        <input value={form.name} onChange={e => set('name')(e.target.value)} required placeholder="Ej: Campaña verano 2026"
          style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: '#ffffff', fontSize: '13px', fontFamily: 'var(--font-body)', outline: 'none', boxSizing: 'border-box' }} />
      </div>

      {/* Descripcion campaña */}
      <div>
        <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
          🧠 Contexto de la campaña <span style={{fontSize:'11px',color:'var(--text-muted)',fontWeight:'400'}}>(la IA usa esto para crear anuncios virales)</span>
        </label>
        <textarea value={form.description} onChange={e => set('description')(e.target.value)} maxLength={5000}
          placeholder="Ej: Vendemos zapatos deportivos para jóvenes. Lanzamos el AirMax Pro a $250.000. Queremos generar ventas directas. Nuestro diferencial es comodidad y estilo urbano."
          style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', border: '1px solid #7c3aed50', color: 'var(--text-primary)', fontSize: '13px', fontFamily: 'var(--font-body)', outline: 'none', boxSizing: 'border-box', minHeight: '90px', resize: 'vertical' }} />
        <div style={{display:'flex',justifyContent:'space-between',marginTop:'4px'}}>
          <div style={{fontSize:'11px',color:'var(--text-muted)'}}>💡 Cuéntale a la IA sobre tu producto, servicio, oferta o lo que quieras promocionar. Más detalle = mejores anuncios.</div>
          <div style={{fontSize:'11px',color:(form.description||'').length>4500?'#ef4444':'var(--text-muted)',flexShrink:0,marginLeft:'8px'}}>{(form.description||'').length}/5000</div>
        </div>
      </div>

      {/* Objetivo */}
      <div>
        <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>Objetivo</label>
        <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: '#3b82f610', border: '1px solid #3b82f640', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px' }}>💡</span>
          <span style={{ fontSize: '12px', color: '#93c5fd' }}>Elige con atención: Meta Ads no permite cambiar el objetivo una vez publicada la campaña. Esta decisión es permanente.</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
          {OBJECTIVES.map(obj => (
            <button type="button" key={obj.id} onClick={() => set('objective')(obj.id)} style={{
              padding: '10px 12px', borderRadius: 'var(--radius-md)', textAlign: 'left', cursor: 'pointer',
              border: `1px solid ${form.objective === obj.id ? '#7c3aed' : 'var(--border)'}`,
              background: form.objective === obj.id ? '#7c3aed15' : 'var(--bg-elevated)',
            }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: form.objective === obj.id ? '#a78bfa' : 'var(--text-primary)' }}>{obj.label}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{obj.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Presupuesto */}
      <div>
        <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>Estrategia de presupuesto</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
          {[
            { id: 'CAMPAIGN', label: 'Presupuesto de la campaña', desc: 'Meta distribuye automáticamente entre los mejores conjuntos de anuncios (Advantage+).' },
            { id: 'ADSET', label: 'Presupuesto del conjunto de anuncios', desc: 'Define un presupuesto diferente para cada conjunto de anuncios.' },
          ].map(opt => (
            <div key={opt.id} onClick={() => set('budget_level')(opt.id)} style={{
              display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 14px',
              borderRadius: 'var(--radius-md)', cursor: 'pointer',
              border: `1px solid ${form.budget_level === opt.id ? '#7c3aed' : 'var(--border)'}`,
              background: form.budget_level === opt.id ? '#7c3aed10' : 'var(--bg-elevated)',
            }}>
              <div style={{ marginTop: '2px', width: '16px', height: '16px', borderRadius: '50%', border: `2px solid ${form.budget_level === opt.id ? '#7c3aed' : 'var(--border)'}`, background: form.budget_level === opt.id ? '#7c3aed' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {form.budget_level === opt.id && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#fff' }} />}
              </div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: form.budget_level === opt.id ? '#a78bfa' : 'var(--text-primary)' }}>{opt.label}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{opt.desc}</div>
              </div>
            </div>
          ))}
        </div>
        {form.budget_level === 'CAMPAIGN' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Tipo</label>
              <select value={form.budget_type} onChange={e => set('budget_type')(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '13px', fontFamily: 'var(--font-body)', outline: 'none' }}>
                <option value="DAILY">Diario</option>
                <option value="LIFETIME">Total de campaña</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Monto</label>
              <input type="number" min="1" step="0.01" value={form.budget_amount} onChange={e => set('budget_amount')(parseFloat(e.target.value))}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: '#ffffff', fontSize: '13px', fontFamily: 'var(--font-body)', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>
        )}
      </div>

      {/* Fechas */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Inicio</label>
          <input type="datetime-local" value={form.start_time} onChange={e => set('start_time')(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '13px', fontFamily: 'var(--font-body)', outline: 'none', colorScheme: 'dark', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Fin (opcional)</label>
          <input type="datetime-local" value={form.end_time} onChange={e => set('end_time')(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '13px', fontFamily: 'var(--font-body)', outline: 'none', colorScheme: 'dark', boxSizing: 'border-box' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '8px' }}>
        <Button type="button" variant="ghost" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" loading={saving}>Crear campaña</Button>
      </div>
    </form>
  );
}

// ── Edit Campaign Form ───────────────────────────────────────
function EditCampaignForm({ campaign, accounts, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    name: campaign.name || '',
    description: campaign.description || '',
    objective: campaign.objective || 'TRAFFIC',
    budget_level: campaign.budget_level || 'CAMPAIGN',
    budget_type: campaign.budget_type || 'DAILY',
    budget_amount: campaign.budget_amount || 10,
    start_time: campaign.start_time ? campaign.start_time.slice(0,16) : '',
    end_time: campaign.end_time ? campaign.end_time.slice(0,16) : '',
  });
  const set = k => v => setForm(f => ({ ...f, [k]: v }));
  return (
    <form onSubmit={e => { e.preventDefault(); onSave(form); }} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div>
        <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Nombre</label>
        <input value={form.name} onChange={e => set('name')(e.target.value)} required
          style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
      </div>
      <div>
        <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
          🧠 Contexto de la campaña <span style={{fontSize:'11px',color:'var(--text-muted)',fontWeight:'400'}}>(la IA usa esto para crear anuncios virales)</span>
        </label>
        <textarea value={form.description} onChange={e => set('description')(e.target.value)} maxLength={5000}
          placeholder="Ej: Vendemos zapatos deportivos para jóvenes. Lanzamos el AirMax Pro a $250.000. Queremos generar ventas directas."
          style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', border: '1px solid #7c3aed50', color: 'var(--text-primary)', fontSize: '13px', fontFamily: 'var(--font-body)', outline: 'none', boxSizing: 'border-box', minHeight: '80px', resize: 'vertical' }} />
        <div style={{display:'flex',justifyContent:'space-between',marginTop:'4px'}}>
          <div style={{fontSize:'11px',color:'var(--text-muted)'}}>💡 Más detalle = mejores anuncios generados por IA</div>
          <div style={{fontSize:'11px',color:(form.description||'').length>4500?'#ef4444':'var(--text-muted)',flexShrink:0,marginLeft:'8px'}}>{(form.description||'').length}/5000</div>
        </div>
      </div>
      <div>
        <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>Objetivo</label>
        <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: '#f59e0b10', border: '1px solid #f59e0b40', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px' }}>⚠️</span>
          <span style={{ fontSize: '12px', color: '#f59e0b' }}>Meta Ads no permite modificar el objetivo de una campaña una vez creada. Este campo es de solo lectura.</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px', opacity: 0.5, pointerEvents: 'none' }}>
          {OBJECTIVES.map(obj => (
            <button type="button" key={obj.id} disabled style={{
              padding: '10px 12px', borderRadius: 'var(--radius-md)', textAlign: 'left', cursor: 'pointer',
              border: `1px solid ${form.objective === obj.id ? '#7c3aed' : 'var(--border)'}`,
              background: form.objective === obj.id ? '#7c3aed15' : 'var(--bg-elevated)',
            }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: form.objective === obj.id ? '#a78bfa' : 'var(--text-primary)' }}>{obj.label}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{obj.desc}</div>
            </button>
          ))}
        </div>
      </div>
      <div>
        <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>Estrategia de presupuesto</label>
        {(campaign.adsets_count ?? 0) > 0 && form.budget_level !== campaign.budget_level ? (
          <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: '#ef444410', border: '1px solid #ef444440', fontSize: '12px', color: '#ef4444', marginBottom: '8px' }}>
            ⚠️ No puedes cambiar la estrategia porque ya existen conjuntos creados con presupuesto asignado.
          </div>
        ) : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
          {[
            { id: 'CAMPAIGN', label: 'Presupuesto de la campaña', desc: 'Meta distribuye automáticamente entre los mejores conjuntos (CBO Advantage+).' },
            { id: 'ADSET', label: 'Presupuesto del conjunto de anuncios', desc: 'Define un presupuesto diferente para cada conjunto.' },
          ].map(opt => (
            <div key={opt.id} onClick={() => { if((campaign.adsets_count ?? 0) > 0) return; set('budget_level')(opt.id); }} style={{
              display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 14px',
              borderRadius: 'var(--radius-md)', cursor: (campaign.adsets_count ?? 0) > 0 ? 'not-allowed' : 'pointer',
              border: `1px solid ${form.budget_level === opt.id ? '#7c3aed' : 'var(--border)'}`,
              background: form.budget_level === opt.id ? '#7c3aed10' : 'var(--bg-elevated)',
              opacity: (campaign.adsets_count ?? 0) > 0 ? 0.7 : 1,
            }}>
              <div style={{ marginTop: '2px', width: '16px', height: '16px', borderRadius: '50%', border: `2px solid ${form.budget_level === opt.id ? '#7c3aed' : 'var(--border)'}`, background: form.budget_level === opt.id ? '#7c3aed' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {form.budget_level === opt.id && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#fff' }} />}
              </div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: form.budget_level === opt.id ? '#a78bfa' : 'var(--text-primary)' }}>{opt.label}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{opt.desc}</div>
              </div>
            </div>
          ))}
        </div>
        {form.budget_level === 'CAMPAIGN' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Tipo presupuesto</label>
              <select value={form.budget_type} onChange={e => set('budget_type')(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '13px', outline: 'none' }}>
                <option value="DAILY">Diario</option>
                <option value="LIFETIME">Total</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Presupuesto <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '400' }}>(editable desde la tarjeta 💰)</span></label>
              <input type="number" value={form.budget_amount} disabled
                style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: '13px', outline: 'none', boxSizing: 'border-box', opacity: 0.6, cursor: 'not-allowed' }} />
            </div>
          </div>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Inicio</label>
          <input type="datetime-local" value={form.start_time} onChange={e => set('start_time')(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', colorScheme: 'dark', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Fin (opcional)</label>
          <input type="datetime-local" value={form.end_time} onChange={e => set('end_time')(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', colorScheme: 'dark', boxSizing: 'border-box' }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <Button type="button" variant="ghost" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" loading={saving}>Guardar cambios</Button>
      </div>
    </form>
  );
}

// ── Campaign Card ─────────────────────────────────────────
function CampaignCard({ campaign, onStatusChange, onDelete, onSelect, selected, onAdSets, onEdit }) {
  const [editBudget, setEditBudget] = useState(false);
  const [newBudget, setNewBudget] = useState(campaign.budget_amount);
  const [loadingBudget, setLoadingBudget] = useState(false);

  const handleBudgetSave = async () => {
    setLoadingBudget(true);
    try {
      await adsAPI.updateBudget(campaign.id, newBudget);
      toast.success('Presupuesto actualizado ✓');
      setEditBudget(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al actualizar');
    }
    setLoadingBudget(false);
  };

  return (
    <div onClick={() => onSelect(campaign)} style={{
      padding: '14px', background: 'var(--bg-card)', border: `1px solid ${selected ? '#7c3aed' : 'var(--border)'}`,
      borderRadius: 'var(--radius-lg)', cursor: 'pointer', transition: 'var(--transition)',
      borderLeft: `3px solid ${NET_COLOR[campaign.network] || '#888'}`,
    }}>
      {/* Fila superior: icono + nombre + badge + acciones */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }} onClick={e => e.stopPropagation()}>
        <NetworkIcon network={campaign.network} size={24} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: '700', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>{campaign.name}</span>
            <Badge color={STATUS_COLOR[campaign.status] || '#888'}>{STATUS_LABEL[campaign.status] || campaign.status}</Badge>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span>📅 {campaign.created_at ? new Date(campaign.created_at).toLocaleDateString('es-CO',{day:'2-digit',month:'short',year:'numeric'}) : '—'} · {campaign.objective} · {campaign.budget_type === 'DAILY' ? 'Diario' : 'Total'}: {fmtCurrency(campaign.budget_amount, campaign.currency)}</span>
            <div style={{ display: 'flex', gap: '3px' }}>
              {(campaign.publisher_platforms || ['facebook']).map(p => (
                <span key={p} title={p} style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: '16px', height: '16px', borderRadius: '3px', fontSize: '10px',
                  background: p==='facebook'?'#1877F220':p==='instagram'?'#E1306C20':p==='messenger'?'#00B2FF20':'#88888820',
                  color: p==='facebook'?'#1877F2':p==='instagram'?'#E1306C':p==='messenger'?'#00B2FF':'#888',
                }}>
                  {p==='facebook'?'𝐟':p==='instagram'?'📷':p==='messenger'?'💬':'●'}
                </span>
              ))}
            </div>
          </div>
        </div>
        {/* Acciones compactas */}
        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          {campaign.status === 'ACTIVE' ? (
            <button onClick={() => onStatusChange(campaign.id, 'PAUSED')} style={{ padding: '6px 8px', borderRadius: '8px', border: 'none', background: '#f59e0b20', color: '#f59e0b', cursor: 'pointer', fontSize: '13px' }}>⏸</button>
          ) : (
            <button onClick={() => onStatusChange(campaign.id, 'ACTIVE')} style={{ padding: '6px 8px', borderRadius: '8px', border: 'none', background: '#10b98120', color: '#10b981', cursor: 'pointer', fontSize: '13px' }}>▶</button>
          )}
          <button onClick={() => onEdit && onEdit(campaign)} style={{ padding: '6px 8px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '13px' }}>✏️</button>
          <button onClick={() => setEditBudget(!editBudget)} style={{ padding: '6px 8px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '13px' }}>💰</button>
          <button onClick={() => onDelete(campaign)} style={{ padding: '6px 8px', borderRadius: '8px', border: '1px solid #ef444430', background: '#ef444415', color: '#ef4444', cursor: 'pointer', fontSize: '13px' }}>🗑</button>

        </div>
      </div>

      {/* Edit budget inline */}
      {editBudget && (
        <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
          <input type="number" value={newBudget} onChange={e => setNewBudget(parseFloat(e.target.value))} min="1" step="0.01"
            style={{ flex: 1, padding: '8px 10px', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '13px', fontFamily: 'var(--font-body)', outline: 'none' }} />
          <Button size="sm" loading={loadingBudget} onClick={handleBudgetSave}>Guardar</Button>
          <Button size="sm" variant="ghost" onClick={() => setEditBudget(false)}>Cancelar</Button>
        </div>
      )}
    </div>
  );
}

// ── Insights Panel ────────────────────────────────────────
function InsightsPanel({ campaign }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preset, setPreset] = useState('last_30d');

  const PRESETS = [
    { id: 'today', label: 'Hoy' },
    { id: 'last_7d', label: '7 días' },
    { id: 'last_30d', label: '30 días' },
    { id: 'last_90d', label: '90 días' },
  ];

  useEffect(() => {
    if (!campaign) return;
    setLoading(true);
    adsAPI.getInsights(campaign.id, preset)
      .then(r => setInsights(r.data))
      .catch(() => setInsights(null))
      .finally(() => setLoading(false));
  }, [campaign?.id, preset]);

  if (!campaign) return (
    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontSize: '14px' }}>
      Selecciona una campaña para ver sus métricas
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: '700', fontSize: '15px' }}>📊 {campaign.name}</h3>
        <div style={{ display: 'flex', gap: '4px' }}>
          {PRESETS.map(p => (
            <button key={p.id} onClick={() => setPreset(p.id)} style={{
              padding: '5px 10px', borderRadius: '7px', fontSize: '12px', fontFamily: 'var(--font-body)', cursor: 'pointer',
              background: preset === p.id ? 'var(--gradient-primary)' : 'var(--bg-elevated)',
              border: `1px solid ${preset === p.id ? 'transparent' : 'var(--border)'}`,
              color: preset === p.id ? '#fff' : 'var(--text-muted)',
            }}>{p.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
          {[...Array(6)].map((_, i) => <div key={i} className="skeleton" style={{ height: '80px', borderRadius: 'var(--radius-md)' }} />)}
        </div>
      ) : insights && typeof insights === 'object' && !Array.isArray(insights) ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px' }}>
            {[
              { label: 'Gasto',        val: fmtCurrency(insights.spend, campaign.currency), icon: '💰', color: '#10b981' },
              { label: 'Impresiones',  val: fmt(insights.impressions), icon: '👁', color: '#7c3aed' },
              { label: 'Alcance',      val: fmt(insights.reach), icon: '📡', color: '#3b82f6' },
              { label: 'Clics',        val: fmt(insights.clicks), icon: '🖱', color: '#f59e0b' },
              { label: 'CTR',          val: `${parseFloat(insights.ctr||0).toFixed(2)}%`, icon: '📈', color: '#06b6d4' },
              { label: 'CPM',          val: fmtCurrency(insights.cpm, campaign.currency), icon: '💵', color: '#ec4899' },
              { label: 'Frecuencia',   val: parseFloat(insights.frequency||0).toFixed(2), icon: '🔄', color: '#8b5cf6' },
            ].map(m => (
              <div key={m.label} style={{ padding: '12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', border: `1px solid ${m.color}30` }}>
                <div style={{ fontSize: '18px', marginBottom: '4px' }}>{m.icon}</div>
                <div style={{ fontWeight: '800', fontSize: '16px', color: m.color }}>{m.val}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{m.label}</div>
              </div>
            ))}
          </div>

          {/* Acciones con CTA */}
          {insights.actions && insights.actions.length > 0 && (
            <div style={{ padding: '14px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
              <h4 style={{ fontSize: '13px', fontWeight: '700', marginBottom: '10px' }}>Acciones realizadas</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {insights.actions.map((a, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{a.action_type.replace(/_/g, ' ')}</span>
                    <span style={{ fontWeight: '700', color: 'var(--text-primary)' }}>{fmt(parseInt(a.value))}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: '13px' }}>
          {insights === null ? 'Sin datos disponibles para este período' : 'Métricas no disponibles — activa la campaña para ver datos'}
        </div>
      )}
    </div>
  );
}



// ── Image Dimension Validator ─────────────────────────────
function validateImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); img.width >= 500 ? resolve(img.width, img.height) : reject(`La imagen debe tener mínimo 500px de ancho (actual: ${img.width}px)`); };
    img.onerror = () => { URL.revokeObjectURL(url); reject('No se pudo leer la imagen'); };
    img.src = url;
  });
}

// ── Video Upload Component ────────────────────────────────
function VideoUploader({ adAccountId, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const [videoFile, setVideoFile] = useState(null);

  const validateVideoDimensions = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => { URL.revokeObjectURL(url); video.videoWidth >= 500 ? resolve() : reject(`El video debe tener mínimo 500px de ancho (actual: ${video.videoWidth}px)`); };
    video.onerror = () => { URL.revokeObjectURL(url); resolve(); };
    video.src = url;
  });

  const handleUpload = async (file) => {
    if (!file) return;
    const maxMB = 4096;
    const sizeMB = file.size / 1024 / 1024;
    if (sizeMB > maxMB) { toast.error('El video supera el límite de 4GB'); return; }
    try { await validateVideoDimensions(file); } catch(err) { toast.error(err); return; }
    setVideoFile(file);
    setUploading(true);
    setProgress('Subiendo video a Meta...');
    try {
      const r = await adsAPI.uploadAdVideo(adAccountId, file);
      const videoId = r.data.video_id;
      setProgress('Video subido. Esperando procesamiento de Meta...');
      // Poll status
      let attempts = 0;
      while (attempts < 30) {
        await new Promise(res => setTimeout(res, 5000));
        const sr = await adsAPI.checkVideoStatus(videoId);
        if (sr.data.status === 'ready') {
          setProgress('✅ Video listo');
          onChange(videoId);
          setUploading(false);
          return;
        }
        if (sr.data.status === 'error') {
          toast.error('Error procesando video en Meta');
          setProgress('');
          setUploading(false);
          return;
        }
        attempts++;
        setProgress(`Procesando... (${attempts * 5}s)`);
      }
      toast.error('Timeout esperando Meta. Intenta de nuevo.');
      setProgress('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error subiendo video');
      setProgress('');
    }
    setUploading(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <label style={{
        display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px',
        borderRadius: '8px', border: `2px dashed ${uploading ? '#7c3aed' : 'var(--border)'}`,
        cursor: uploading ? 'not-allowed' : 'pointer', color: 'var(--text-muted)', fontSize: '13px',
        background: uploading ? '#7c3aed10' : 'transparent',
      }}>
        <span style={{ fontSize: '20px' }}>{uploading ? '⏳' : '🎬'}</span>
        <div>
          <div>{videoFile ? videoFile.name : 'Seleccionar video (MP4, MOV — máx 4GB)'}</div>
          {progress && <div style={{ fontSize: '11px', color: '#7c3aed', marginTop: '2px' }}>{progress}</div>}
        </div>
        <input type="file" accept="video/mp4,video/mov,video/quicktime,video/*" style={{ display: 'none' }} disabled={uploading} onChange={e => handleUpload(e.target.files[0])} />
      </label>
      {value && !uploading && (
        <div style={{ fontSize: '12px', color: '#10b981', display: 'flex', alignItems: 'center', gap: '6px' }}>
          ✅ Video ID: <code style={{ background: 'var(--bg-elevated)', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' }}>{value}</code>
        </div>
      )}
      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
        Formatos: MP4, MOV · Duración: 1s–240min · Relación: 9:16, 1:1, 16:9
      </div>
    </div>
  );
}

// ── Interest Search Component ────────────────────────────
function InterestSearch({ interests, onChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const search = async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const { default: api } = await import('../api/client');
      const r = await api.get(`/ads/targeting/interests?q=${encodeURIComponent(q)}`);
      const data = r.data;
      setResults(Array.isArray(data) ? data : (data.interests || data.data || []));
    } catch { setResults([]); }
    setSearching(false);
  };

  const add = (item) => {
    if (!interests.find(i => i.id === item.id)) onChange([...interests, item]);
    setQuery(''); setResults([]);
  };
  const remove = (id) => onChange(interests.filter(i => i.id !== id));

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <input value={query} onChange={e => { setQuery(e.target.value); search(e.target.value); }}
          placeholder="Buscar interés (ej: fitness, tecnología, viajes...)"
          style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '13px', fontFamily: 'var(--font-body)', outline: 'none', boxSizing: 'border-box' }} />
        {searching && <span style={{ position: 'absolute', right: '10px', top: '10px', fontSize: '11px', color: 'var(--text-muted)' }}>Buscando...</span>}
        {results.length > 0 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', zIndex: 50, maxHeight: '180px', overflowY: 'auto', marginTop: '4px' }}>
            {results.map(r => (
              <div key={r.id} onClick={() => add(r)} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid var(--border)' }}
                onMouseEnter={e => e.target.style.background='var(--bg-elevated)'} onMouseLeave={e => e.target.style.background='transparent'}>
                {r.name} <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>({fmt(r.audience_size_lower_bound||0)}–{fmt(r.audience_size_upper_bound||0)})</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {interests.length > 0 && (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
          {interests.map(i => (
            <span key={i.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '6px', background: '#7c3aed20', border: '1px solid #7c3aed', color: '#7c3aed', fontSize: '12px' }}>
              {i.name} <button onClick={() => remove(i.id)} style={{ background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: 0 }}>×</button>
            </span>
          ))}
        </div>
      )}
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>Segmenta por intereses para mejorar el rendimiento de tu campaña.</div>
    </div>
  );
}

// ── Create AdSet Form ─────────────────────────────────────
function CreateAdSetForm({ campaignId, adAccountId, onSave, onCancel, saving, budgetLevel = 'ADSET', campaign = null }) {
  const [ariaResult, setAriaResult] = React.useState(null);
  const [ariaLoading, setAriaLoading] = React.useState(false);
  const [suggestedInterests, setSuggestedInterests] = useState([]);
  const [form, setForm] = useState({
    campaign_id: campaignId,
    ad_account_id: adAccountId,
    name: '',
    budget_amount: 0,
    budget_type: 'DAILY',
    budget_level: budgetLevel,
    age_min: 18,
    age_max: 65,
    countries: ['CO'],
    optimization_goal: 'REACH',
    billing_event: 'IMPRESSIONS',
    publisher_platforms: ['facebook', 'instagram'],
    facebook_positions: ['feed', 'story'],
    instagram_positions: ['stream', 'story', 'reels'],
    genders: [],
    interests: [],
    messenger_positions: ['messenger_inbox'],
    audience_network_positions: ['classic'],
  });
  const GOAL_MAP = {
    AWARENESS: 'REACH', TRAFFIC: 'LINK_CLICKS', ENGAGEMENT: 'POST_ENGAGEMENT',
    LEADS: 'LEAD_GENERATION', APP_PROMOTION: 'APP_INSTALLS', SALES: 'OFFSITE_CONVERSIONS',
    VIDEO_VIEWS: 'VIDEO_VIEWS', CONVERSIONS: 'OFFSITE_CONVERSIONS', REACH: 'REACH',
  };
  const GOAL_LABELS = {
    REACH: 'Alcance', LINK_CLICKS: 'Clics al enlace', POST_ENGAGEMENT: 'Interacción',
    LEAD_GENERATION: 'Generación de leads', APP_INSTALLS: 'Instalaciones de app',
    OFFSITE_CONVERSIONS: 'Conversiones', VIDEO_VIEWS: 'Reproducciones de video',
    LANDING_PAGE_VIEWS: 'Vistas de página destino', IMPRESSIONS: 'Impresiones',
  };
  const suggestedGoal = campaign?.objective ? (GOAL_MAP[campaign.objective] || 'REACH') : null;
  const COUNTRIES = { CO: '🇨🇴 Colombia', MX: '🇲🇽 México', ES: '🇪🇸 España', AR: '🇦🇷 Argentina', US: '🇺🇸 EE.UU', CL: '🇨🇱 Chile', PE: '🇵🇪 Perú', VE: '🇻🇪 Venezuela' };
  const PLATFORMS = [
    { id: 'facebook', label: 'Facebook', color: '#1877F2', icon: '𝐟' },
    { id: 'instagram', label: 'Instagram', color: '#E1306C', icon: '📷' },
    { id: 'audience_network', label: 'Audience Network', color: '#1877F2', icon: '🌐' },
    { id: 'messenger', label: 'Messenger', color: '#00B2FF', icon: '💬' },
    { id: 'whatsapp', label: 'WhatsApp', color: '#25D366', icon: '📱' },
    { id: 'threads', label: 'Threads', color: '#FFFFFF', icon: '🧵' },
  ];
  const FB_POSITIONS = ['feed','story','marketplace','right_hand_column','facebook_reels','instream_video'];
  const IG_POSITIONS = ['stream','story','reels','explore','explore_home','ig_search'];
  const MSG_POSITIONS = ['messenger_inbox','messenger_stories'];
  const AN_POSITIONS = ['classic','instream_video','rewarded_video'];

  const togglePlatform = (id) => setForm(f => {
    const current = f.publisher_platforms;
    let platforms = current.includes(id) ? current.filter(p => p !== id) : [...current, id];
    let ig_pos = f.instagram_positions || [];
    // Dependencias: WhatsApp requiere Instagram Stories
    if (id === 'whatsapp' && !current.includes('whatsapp')) {
      if (!platforms.includes('instagram')) platforms = [...platforms, 'instagram'];
      if (!ig_pos.includes('story')) ig_pos = [...ig_pos, 'story'];
    }
    // Threads requiere Instagram Feed
    if (id === 'threads' && !current.includes('threads')) {
      if (!platforms.includes('instagram')) platforms = [...platforms, 'instagram'];
      if (!ig_pos.includes('stream')) ig_pos = [...ig_pos, 'stream'];
    }
    if (id === 'audience_network' && !current.includes('audience_network')) {
      if (!platforms.includes('facebook')) platforms = [...platforms, 'facebook'];
      const fb_pos = f.facebook_positions || [];
      if (!fb_pos.includes('feed')) return {...f, publisher_platforms: platforms, instagram_positions: ig_pos, facebook_positions: [...fb_pos, 'feed']};
    }
    return {...f, publisher_platforms: platforms, instagram_positions: ig_pos};
  });
  const toggleFbPos = (pos) => setForm(f => ({
    ...f, facebook_positions: f.facebook_positions.includes(pos)
      ? f.facebook_positions.filter(p => p !== pos)
      : [...f.facebook_positions, pos]
  }));
  const toggleIgPos = (pos) => setForm(f => ({
    ...f, instagram_positions: f.instagram_positions.includes(pos)
      ? f.instagram_positions.filter(p => p !== pos)
      : [...f.instagram_positions, pos]
  }));
  const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '14px', fontFamily: 'var(--font-body)', outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' };

  const toggleCountry = (code) => {
    setForm(f => ({
      ...f,
      countries: f.countries.includes(code) ? f.countries.filter(c => c !== code) : [...f.countries, code]
    }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div>
        <label style={labelStyle}>Nombre del conjunto</label>
        <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Ej: Audiencia 18-35 Colombia" required />
      </div>
      {budgetLevel === 'ADSET' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div><label style={labelStyle}>Presupuesto ({form.budget_type === 'DAILY' ? 'diario' : 'total'})</label><input style={inputStyle} type="number" min="1" step="0.01" placeholder="Ej: 30000" value={form.budget_amount || ''} onChange={e => setForm(f => ({...f, budget_amount: parseFloat(e.target.value), budget_level: 'ADSET'}))} /></div>
          <div><label style={labelStyle}>Tipo</label>
            <select style={inputStyle} value={form.budget_type} onChange={e => setForm(f => ({...f, budget_type: e.target.value}))}>
              <option value="DAILY">Diario</option><option value="LIFETIME">Total campaña</option>
            </select>
          </div>
        </div>
      ) : (
        <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#7c3aed10', border: '1px solid #7c3aed30', fontSize: '12px', color: '#a78bfa' }}>
          💡 El presupuesto lo administra la campaña automáticamente (CBO Advantage+).
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div><label style={labelStyle}>Edad mínima</label><input style={inputStyle} type="number" min="13" max="65" value={form.age_min} onChange={e => setForm(f => ({...f, age_min: parseInt(e.target.value)}))} /></div>
        <div><label style={labelStyle}>Edad máxima</label><input style={inputStyle} type="number" min="13" max="65" value={form.age_max} onChange={e => setForm(f => ({...f, age_max: parseInt(e.target.value)}))} /></div>
      </div>
      {/* Redes / Placements */}
      <div>
        <label style={labelStyle}>Redes sociales</label>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
          {PLATFORMS.map(p => (
            <button type="button" key={p.id} onClick={() => togglePlatform(p.id)} style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
              borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontFamily: 'var(--font-body)',
              background: form.publisher_platforms.includes(p.id) ? `${p.color}20` : 'var(--bg-elevated)',
              border: `1px solid ${form.publisher_platforms.includes(p.id) ? p.color : 'var(--border)'}`,
              color: form.publisher_platforms.includes(p.id) ? p.color : 'var(--text-muted)',
            }}>
              <span style={{fontSize:'16px'}}>{p.icon}</span>{p.label}
            </button>
          ))}
        </div>
        {form.publisher_platforms.includes('facebook') && (
          <div style={{marginBottom:'8px'}}>
            <div style={{fontSize:'12px',color:'#1877F2',fontWeight:'600',marginBottom:'4px'}}>𝐟 Ubicaciones Facebook</div>
            <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
              {FB_POSITIONS.map(pos => (
                <button type="button" key={pos} onClick={() => toggleFbPos(pos)} style={{
                  padding:'4px 8px',borderRadius:'6px',fontSize:'11px',cursor:'pointer',
                  background: form.facebook_positions.includes(pos) ? '#1877F220' : 'var(--bg-elevated)',
                  border: `1px solid ${form.facebook_positions.includes(pos) ? '#1877F2' : 'var(--border)'}`,
                  color: form.facebook_positions.includes(pos) ? '#1877F2' : 'var(--text-muted)',
                }}>{pos}</button>
              ))}
            </div>
          </div>
        )}
        {form.publisher_platforms.includes('instagram') && (
          <div>
            <div style={{fontSize:'12px',color:'#E1306C',fontWeight:'600',marginBottom:'4px'}}>📷 Ubicaciones Instagram</div>
            <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
              {IG_POSITIONS.map(pos => (
                <button type="button" key={pos} onClick={() => toggleIgPos(pos)} style={{
                  padding:'4px 8px',borderRadius:'6px',fontSize:'11px',cursor:'pointer',
                  background: form.instagram_positions.includes(pos) ? '#E1306C20' : 'var(--bg-elevated)',
                  border: `1px solid ${form.instagram_positions.includes(pos) ? '#E1306C' : 'var(--border)'}`,
                  color: form.instagram_positions.includes(pos) ? '#E1306C' : 'var(--text-muted)',
                }}>{pos}</button>
              ))}
            </div>
          </div>
        )}
        {form.publisher_platforms.includes('messenger') && (
          <div style={{marginBottom:'8px'}}>
            <div style={{fontSize:'12px',color:'#00B2FF',fontWeight:'600',marginBottom:'4px'}}>💬 Ubicaciones Messenger</div>
            <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
              {MSG_POSITIONS.map(pos => (
                <button type="button" key={pos} onClick={() => setForm(f => ({...f, messenger_positions: (f.messenger_positions||[]).includes(pos) ? (f.messenger_positions||[]).filter(p=>p!==pos) : [...(f.messenger_positions||[]),pos]}))} style={{
                  padding:'4px 8px',borderRadius:'6px',fontSize:'11px',cursor:'pointer',
                  background:(form.messenger_positions||[]).includes(pos)?'#00B2FF20':'var(--bg-elevated)',
                  border:`1px solid ${(form.messenger_positions||[]).includes(pos)?'#00B2FF':'var(--border)'}`,
                  color:(form.messenger_positions||[]).includes(pos)?'#00B2FF':'var(--text-muted)',
                }}>{pos}</button>
              ))}
            </div>
          </div>
        )}
        {form.publisher_platforms.includes('audience_network') && (
          <div style={{marginBottom:'8px'}}>
            <div style={{fontSize:'12px',color:'#1877F2',fontWeight:'600',marginBottom:'4px'}}>🌐 Ubicaciones Audience Network</div>
            <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
              {AN_POSITIONS.map(pos => (
                <button type="button" key={pos} onClick={() => setForm(f => ({...f, audience_network_positions: (f.audience_network_positions||[]).includes(pos) ? (f.audience_network_positions||[]).filter(p=>p!==pos) : [...(f.audience_network_positions||[]),pos]}))} style={{
                  padding:'4px 8px',borderRadius:'6px',fontSize:'11px',cursor:'pointer',
                  background:(form.audience_network_positions||[]).includes(pos)?'#1877F220':'var(--bg-elevated)',
                  border:`1px solid ${(form.audience_network_positions||[]).includes(pos)?'#1877F2':'var(--border)'}`,
                  color:(form.audience_network_positions||[]).includes(pos)?'#1877F2':'var(--text-muted)',
                }}>{pos}</button>
              ))}
            </div>
          </div>
        )}
        {form.publisher_platforms.includes('whatsapp') && (
          <div style={{marginBottom:'8px',padding:'8px 12px',background:'#25D36615',borderRadius:'8px',border:'1px solid #25D36630'}}>
            <div style={{fontSize:'12px',color:'#25D366',fontWeight:'600'}}>📱 WhatsApp — Estado de WhatsApp (requiere Instagram Stories)</div>
          </div>
        )}
        {form.publisher_platforms.includes('threads') && (
          <div style={{marginBottom:'8px',padding:'8px 12px',background:'#00000015',borderRadius:'8px',border:'1px solid #00000030'}}>
            <div style={{fontSize:'12px',color:'var(--text-primary)',fontWeight:'600'}}>🧵 Threads — Feed de Threads (requiere Instagram Feed)</div>
          </div>
        )}
      </div>

      <div>
        <label style={labelStyle}>Meta de optimización
          {suggestedGoal && suggestedGoal !== form.optimization_goal && (
            <span onClick={() => setForm(f => ({...f, optimization_goal: suggestedGoal}))}
              style={{marginLeft:'8px',fontSize:'11px',color:'#a78bfa',cursor:'pointer',fontWeight:'400',background:'#7c3aed20',padding:'2px 8px',borderRadius:'6px'}}>
              ✨ Sugerido: {GOAL_LABELS[suggestedGoal]}
            </span>
          )}
        </label>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {Object.entries(GOAL_LABELS).map(([val, lbl]) => (
            <button type="button" key={val} onClick={() => setForm(f => ({...f, optimization_goal: val}))} style={{
              padding: '5px 10px', borderRadius: '7px', fontSize: '12px', cursor: 'pointer',
              background: form.optimization_goal === val ? '#7c3aed20' : 'var(--bg-elevated)',
              border: `1px solid ${form.optimization_goal === val ? '#7c3aed' : 'var(--border)'}`,
              color: form.optimization_goal === val ? '#7c3aed' : 'var(--text-muted)',
            }}>{lbl}</button>
          ))}
        </div>
      </div>

      <div>
        <label style={labelStyle}>Países objetivo</label>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {Object.entries(COUNTRIES).map(([code, label]) => (
            <button type="button" key={code} onClick={() => toggleCountry(code)} style={{
              padding: '5px 10px', borderRadius: '7px', fontSize: '12px', cursor: 'pointer',
              background: form.countries.includes(code) ? '#7c3aed20' : 'var(--bg-elevated)',
              border: `1px solid ${form.countries.includes(code) ? '#7c3aed' : 'var(--border)'}`,
              color: form.countries.includes(code) ? '#7c3aed' : 'var(--text-muted)',
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* Géneros */}
      <div>
        <label style={labelStyle}>Género objetivo</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[{id:0,label:'Todos'},{id:1,label:'Hombres'},{id:2,label:'Mujeres'}].map(g => (
            <button type="button" key={g.id} onClick={() => setForm(f => ({...f, genders: g.id === 0 ? [] : [g.id]}))} style={{
              padding:'7px 14px', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontFamily:'var(--font-body)',
              background: (g.id === 0 ? (!form.genders||form.genders.length===0) : form.genders?.includes(g.id)) ? '#7c3aed20' : 'var(--bg-elevated)',
              border: `1px solid ${(g.id === 0 ? (!form.genders||form.genders.length===0) : form.genders?.includes(g.id)) ? '#7c3aed' : 'var(--border)'}`,
              color: (g.id === 0 ? (!form.genders||form.genders.length===0) : form.genders?.includes(g.id)) ? '#7c3aed' : 'var(--text-muted)',
            }}>{g.label}</button>
          ))}
        </div>
      </div>
      {/* Intereses */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
          <label style={{...labelStyle, marginBottom: 0}}>Intereses y comportamientos <span style={{fontSize:'11px',color:'var(--text-muted)',fontWeight:'400'}}>(opcional pero recomendado)</span></label>
          <button type="button" onClick={async () => {
            try {
              const { default: api } = await import('../api/client');
              const { toast: t } = await import('react-hot-toast');
              const toastId = t.loading('ARIA buscando intereses en Meta...');
              const { data } = await api.post('/ads/suggest-interests', {
                campaign_description: campaign?.description || '',
                objective: campaign?.objective || 'TRAFFIC',
                countries: form.countries,
              });
              t.dismiss(toastId);
              if (data.interests?.length) {
                setSuggestedInterests(data.interests);
                setForm(f => {
                  const current = f.interests || [];
                  const newOnes = data.interests.filter(ni => !current.find(ci => ci.id === ni.id));
                  return {...f, interests: [...current, ...newOnes]};
                });
                t.success(`✅ ${data.interests.length} intereses agregados por ARIA`);
              }
            } catch(e) { const { toast: t } = await import('react-hot-toast'); t.error('Error consultando ARIA'); }
          }} style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', background: '#7c3aed', color: '#fff', fontSize: '11px', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}>
            ✨ ARIA
          </button>
        </div>
        {suggestedInterests.length > 0 && (
          <div style={{ marginBottom: '8px', padding: '10px 12px', background: 'linear-gradient(135deg,#1e1040,#2d1b69)', border: '1px solid #7c3aed40', borderRadius: '8px' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#a78bfa', marginBottom: '6px' }}>🎯 Intereses sugeridos por ARIA:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {suggestedInterests.map((interest, i) => (
                <span key={i} style={{ padding: '3px 10px', borderRadius: '6px', background: '#7c3aed20', border: '1px solid #7c3aed50', fontSize: '12px', color: '#e2d9f3' }}>{typeof interest === "object" ? interest.name : interest}</span>
              ))}
            </div>
            <div style={{fontSize:'11px',color:'var(--text-muted)',marginTop:'6px'}}>💡 Búscalos en el campo de abajo para agregarlos</div>
          </div>
        )}
        <InterestSearch interests={form.interests||[]} onChange={v => setForm(f => ({...f, interests: v}))} />
      </div>
      {/* Panel ARIA */}
      {(() => {
        return (
          <div style={{ borderRadius: '12px', border: '1px solid #7c3aed40', overflow: 'hidden' }}>
            <button type="button" disabled={ariaLoading} onClick={async () => {
              setAriaLoading(true); setAriaResult(null);
              try {
                const { default: api } = await import('../api/client');
                const { data } = await api.post('/ads/analyze-adset', {
                  campaign_description: campaign?.description || '',
                  objective: campaign?.objective || 'TRAFFIC',
                  optimization_goal: form.optimization_goal,
                  age_min: form.age_min, age_max: form.age_max,
                  genders: form.genders || [],
                  countries: form.countries,
                  publisher_platforms: form.publisher_platforms,
                  facebook_positions: form.facebook_positions || [],
                  instagram_positions: form.instagram_positions || [],
                  interests: form.interests || [],
                  budget_level: budgetLevel,
                  budget_amount: form.budget_amount || 0,
                  budget_type: form.budget_type || 'DAILY',
                });
                if (data.suggestion) {
                  setAriaResult({ text: data.suggestion, suggested_budget: data.suggested_budget });
                  if (data.suggested_budget && budgetLevel === 'ADSET') setForm(f => ({...f, budget_amount: data.suggested_budget}));
                }
              } catch { toast.error('Error analizando conjunto'); }
              setAriaLoading(false);
            }} style={{ width: '100%', padding: '12px 16px', background: 'linear-gradient(135deg, #1e1040, #2d1b69)', border: 'none', cursor: ariaLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '18px' }}>{ariaLoading ? '⏳' : '✨'}</span>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#e2d9f3' }}>{ariaLoading ? 'ARIA analizando...' : 'ARIA — Evaluar conjunto'}</div>
                <div style={{ fontSize: '11px', color: '#a78bfa' }}>{budgetLevel === 'ADSET' ? 'Analiza segmentación y sugiere presupuesto óptimo' : 'Evalúa tu segmentación y da recomendaciones'}</div>
              </div>
              {!ariaLoading && <span style={{ marginLeft: 'auto', fontSize: '18px', color: '#7c3aed' }}>›</span>}
            </button>
            {ariaResult && (
              <div style={{ padding: '16px', background: '#0d0820', borderTop: '1px solid #7c3aed30' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#a78bfa' }}>🎯 Análisis de ARIA</span>
                  <button type="button" onClick={() => setAriaResult(null)} style={{ background: 'none', border: 'none', color: '#a78bfa', cursor: 'pointer', fontSize: '16px' }}>✕</button>
                </div>
                <div style={{ fontSize: '13px', color: '#e2d9f3', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>{ariaResult.text}</div>
                {ariaResult.suggested_budget && budgetLevel === 'ADSET' && (
                  <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '8px', background: '#10b98115', border: '1px solid #10b98140', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px' }}>💰</span>
                    <span style={{ fontSize: '12px', color: '#10b981', fontWeight: '600' }}>Presupuesto sugerido aplicado: {ariaResult.suggested_budget.toLocaleString('es-CO')} COP</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
        <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
        <button disabled={saving} onClick={() => { if(saving) return; if(!form.name.trim()){toast.error("El nombre es requerido");return;} if(form.countries.length===0){toast.error("Selecciona al menos un país");return;} if(form.publisher_platforms.length===0){toast.error("Selecciona al menos una plataforma");return;} onSave(form); }} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'8px 18px', borderRadius:'8px', border:'none', cursor: saving ? 'not-allowed' : 'pointer', background: saving ? '#7c3aed90' : '#7c3aed', color:'#fff', fontSize:'13px', fontWeight:'700', fontFamily:'var(--font-body)', transition:'all 0.2s' }}>
          {saving ? (<><span style={{ width:'14px', height:'14px', border:'2px solid #ffffff50', borderTop:'2px solid #fff', borderRadius:'50%', display:'inline-block', animation:'spin 0.7s linear infinite' }} />Creando en Meta...</>) : '📦 Crear conjunto'}
        </button>
      </div>
    </div>
  );
}

// ── Create Ad Form ────────────────────────────────────────
function CreateAdForm({ adSetId, adAccountId, onSave, onCancel, saving, campaign, adset, pixelId = null }) {
  const [generatingAI, setGeneratingAI] = useState(false);
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [aiData, setAiData] = useState(null);

  const handleGenerateAI = async () => {
    setGeneratingAI(true);
    try {
      const { default: api } = await import('../api/client');
      const payload = {
        campaign_description: campaign?.description || '',
        // DEBUG
        ...(console.log('CAMPAIGN DESC:', campaign?.description) || {}),
        objective: campaign?.objective || 'TRAFFIC',
        optimization_goal: adset?.optimization_goal || '',
        age_min: adset?.age_min || 18,
        age_max: adset?.age_max || 65,
        countries: adset?.countries || [],
        publisher_platforms: adset?.publisher_platforms || form.publisher_platforms || [],
        image_url: form.image_url || '',
        media_description: form.image_url ? 'Imagen del anuncio cargada' : '',
        tone: 'professional',
      };
      const { data } = await api.post('/ads/generate-copy', payload);
      setForm(f => ({ ...f,
        headline: data.headline || f.headline,
        body: data.body || f.body,
        description: data.description || f.description,
      }));
      if (data.recommendations?.length) setAiRecommendations(data.recommendations);
      setAiData(data);
      const { toast: t } = await import('react-hot-toast'); t.success('Copy generado con IA ✨');
    } catch(e) {
      const { toast: t } = await import('react-hot-toast'); t.error(e?.response?.data?.detail || 'Error generando copy');
    }
    setGeneratingAI(false);
  };
  const [form, setForm] = useState({
    ad_set_id: adSetId,
    ad_account_id: adAccountId,
    name: '',
    ad_type: 'image',
    headline: '',
    body: '',
    description: '',
    destination_url: '',
    image_url: '',
    call_to_action: 'LEARN_MORE',
    publisher_platforms: ['facebook', 'instagram'],
    placements: { facebook: ['feed','story'], instagram: ['stream','story'] },
  });
  const [previewPlatform, setPreviewPlatform] = useState('facebook');
  const [showPreview, setShowPreview] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '14px', fontFamily: 'var(--font-body)', outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' };
  const CTAS = [{v:'LEARN_MORE',l:'Más información'},{v:'SHOP_NOW',l:'Comprar ahora'},{v:'SIGN_UP',l:'Registrarse'},{v:'CONTACT_US',l:'Contáctenos'},{v:'BOOK_TRAVEL',l:'Reservar viaje'},{v:'DOWNLOAD',l:'Descargar'},{v:'GET_OFFER',l:'Ver oferta'},{v:'WATCH_MORE',l:'Ver más'},{v:'SUBSCRIBE',l:'Suscribirse'},{v:'GET_QUOTE',l:'Obtener cotización'},{v:'SEND_MESSAGE',l:'Enviar mensaje'}];

  const handleImageUpload = async (file) => {
    if (!file) return;
    try { await validateImageDimensions(file); } catch(err) { toast.error(err); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { default: api } = await import('../api/client'); const { data } = await api.post('/posts/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = data.url || data.media_url || data.path || (data.media_id ? `${window.location.origin}/uploads/${data.media_id}` : null);
      setForm(f => ({ ...f, image_url: url }));
      setImageFile(file);
    } catch (e) {
      toast.error('Error subiendo imagen');
    }
    setUploading(false);
  };

  const validate = () => {
    if (!form.name.trim()) { toast.error('El nombre es requerido'); return false; }
    if (!form.destination_url.trim()) { toast.error('La URL destino es requerida'); return false; }
    try { const u = new URL(form.destination_url); if (!['http:','https:'].includes(u.protocol)) throw new Error(); const parts = u.hostname.split('.'); if (parts.length < 2 || parts[parts.length-1].length < 2 || !/^[a-zA-Z]+$/.test(parts[parts.length-1])) throw new Error(); } catch(e) { toast.error('La URL destino no es valida. Ejemplo: https://tusitio.com'); return false; }
    if (!form.image_url.trim()) { toast.error(form.ad_type === 'video' ? 'Sube el video primero' : 'La imagen es requerida'); return false; }
    return true;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

      {/* Tipo */}
      <div>
        <label style={labelStyle}>Tipo de anuncio</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[{id:'image',label:'🖼 Imagen'},{id:'video',label:'🎬 Video'}].map(t => (
            <button type="button" key={t.id} onClick={() => setForm(f => ({...f, ad_type: t.id, image_url: ''}))} style={{
              padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '13px',
              background: form.ad_type === t.id ? '#7c3aed20' : 'var(--bg-elevated)',
              border: `1px solid ${form.ad_type === t.id ? '#7c3aed' : 'var(--border)'}`,
              color: form.ad_type === t.id ? '#7c3aed' : 'var(--text-muted)',
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Nombre */}
      <div>
        <label style={labelStyle}>Nombre del anuncio <span style={{color:'#ef4444',fontSize:'11px'}}>*</span></label>
        <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Ej: Anuncio verano 2026" />
      </div>

      {/* Imagen / Video */}
      {form.ad_type === 'image' ? (
        <div>
          <label style={labelStyle}>Imagen <span style={{color:'#ef4444',fontSize:'11px'}}>*</span></label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '8px', border: '2px dashed var(--border)', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '13px' }}>
              <span>📁</span>
              <span>{imageFile ? imageFile.name : uploading ? 'Subiendo...' : 'Seleccionar imagen (JPG, PNG — mín. 500px ancho, recomendado 1200x628px)'}</span>
              <input type="file" accept="image/*" style={{display:'none'}} onChange={e => handleImageUpload(e.target.files[0])} />
            </label>
            {form.image_url && <div style={{fontSize:'11px',color:'#10b981'}}>✓ Imagen cargada: {form.image_url.split('/').pop()}</div>}
            <div style={{fontSize:'11px',color:'var(--text-muted)'}}>O pega una URL:</div>
            <input style={inputStyle} value={form.image_url} onChange={e => setForm(f => ({...f, image_url: e.target.value}))} placeholder="https://tusitio.com/imagen.jpg" />
          </div>
        </div>
      ) : (
        <>
        <div>
          <label style={labelStyle}>Video <span style={{color:'#ef4444',fontSize:'11px'}}>*</span></label>
          <VideoUploader adAccountId={adAccountId} value={form.image_url} onChange={v => setForm(f => ({...f, image_url: v}))} />
        </div>
        <div>
          <label style={labelStyle}>Miniatura del video <span style={{color:'#ef4444',fontSize:'11px'}}>* requerida por Meta</span></label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '8px', border: '2px dashed var(--border)', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '13px' }}>
            <span>🖼</span>
            <span>{form.thumbnail_file ? form.thumbnail_file : 'Seleccionar miniatura (JPG, PNG — mín. 500px ancho)'}</span>
            <input type="file" accept="image/*" style={{display:'none'}} onChange={async e => {
              const file = e.target.files[0];
              if (!file) return;
              try { await validateImageDimensions(file); } catch(err) { toast.error(err); return; }
              const fd = new FormData(); fd.append('file', file);
              try {
                const { default: api } = await import('../api/client');
                const { data } = await api.post('/posts/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                const url = data.url || data.media_url || data.path || (data.media_id ? `${window.location.origin}/uploads/${data.media_id}` : null);
                setForm(f => ({ ...f, thumbnail_url: url, thumbnail_file: file.name }));
              } catch { toast.error('Error subiendo miniatura'); }
            }} />
          </label>
          <div style={{fontSize:'11px',color:'var(--text-muted)',marginTop:'4px'}}>JPG o PNG · Mín. 500px ancho · Recomendado 1200x628px · Relación 1.91:1 o 1:1</div>
          {form.thumbnail_url && <div style={{fontSize:'11px',color:'#10b981',marginTop:'4px'}}>✅ Miniatura: {form.thumbnail_url.split('/').pop()}</div>}
        </div>
        </>
      )}

      {/* IA Copy Generator */}
      <div style={{ padding: '12px 14px', borderRadius: '10px', background: '#7c3aed10', border: '1px solid #7c3aed30', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#a78bfa' }}>✨ Generar copy con IA</div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>La IA usará el objetivo, audiencia y URL para crear headline, texto y descripción optimizados.</div>
        </div>
        <button type="button" onClick={() => { setAiRecommendations([]); setAiData(null); handleGenerateAI(); }} disabled={generatingAI}
          style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: generatingAI ? '#4c1d95' : '#7c3aed', color: '#fff', cursor: generatingAI ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {generatingAI ? 'Generando...' : '✨ Generar'}
        </button>
      </div>

      {aiRecommendations.length > 0 && (
        <div style={{ background: 'linear-gradient(135deg,#1e1040,#2d1b69)', border: '1px solid #7c3aed50', borderRadius: '12px', padding: '16px', position: 'relative' }}>
          <button onClick={() => setAiRecommendations([])} style={{ position: 'absolute', top: '10px', right: '12px', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}>×</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
            <span style={{ fontSize: '16px' }}>🧠</span>
            <span style={{ fontSize: '13px', fontWeight: '700', color: '#a78bfa' }}>ARIA — Tu asesora experta en Meta Ads</span>
          </div>

          {/* Score de creatividad */}
          {aiData?.creativity_score && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', padding: '10px 12px', background: '#ffffff08', borderRadius: '8px' }}>
              <div style={{ textAlign: 'center', flexShrink: 0 }}>
                <div style={{ fontSize: '24px', fontWeight: '800', color: aiData.creativity_score >= 8 ? '#10b981' : aiData.creativity_score >= 6 ? '#f59e0b' : '#ef4444' }}>{aiData.creativity_score}<span style={{fontSize:'14px'}}>/10</span></div>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Score</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#e2d9f3' }}>Potencial del anuncio</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{aiData.creativity_score_reason}</div>
              </div>
            </div>
          )}

          {/* Fase de aprendizaje */}
          {aiData?.learning_phase_days && (
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '12px', padding: '10px 12px', background: '#f59e0b10', border: '1px solid #f59e0b30', borderRadius: '8px' }}>
              <span style={{ fontSize: '16px' }}>⏳</span>
              <div>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#f59e0b' }}>Fase de aprendizaje: {aiData.learning_phase_days} días</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{aiData.learning_phase_tip}</div>
              </div>
            </div>
          )}

          {/* Variantes A/B */}
          {aiData?.ab_variants?.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#a78bfa', marginBottom: '6px' }}>🔀 Variantes A/B — Titular:</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {aiData.ab_variants.map((v, i) => (
                  <div key={i} onClick={() => setForm(f => ({...f, headline: v}))} style={{ padding: '7px 12px', background: '#7c3aed15', border: '1px solid #7c3aed40', borderRadius: '7px', fontSize: '12px', color: '#e2d9f3', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{v}</span><span style={{color:'#7c3aed',fontSize:'11px'}}>Usar →</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {aiData?.ab_body_variants?.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#a78bfa', marginBottom: '6px' }}>🔀 Variantes A/B — Texto principal:</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {aiData.ab_body_variants.map((v, i) => (
                  <div key={i} style={{ padding: '7px 12px', background: '#ffffff08', border: '1px solid #7c3aed30', borderRadius: '7px', fontSize: '12px', color: '#e2d9f3', lineHeight: '1.5' }}>
                    <div style={{fontSize:'10px',color:'#7c3aed',marginBottom:'3px',fontWeight:'600'}}>Variante {String.fromCharCode(65+i)}</div>
                    {v}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Intereses sugeridos */}
          {aiData?.suggested_interests?.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#a78bfa', marginBottom: '6px' }}>🎯 Intereses sugeridos para segmentar:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {aiData.suggested_interests.map((interest, i) => (
                  <span key={i} style={{ padding: '4px 10px', borderRadius: '6px', background: '#7c3aed15', border: '1px solid #7c3aed40', fontSize: '12px', color: '#e2d9f3' }}>{typeof interest === "object" ? interest.name : interest}</span>
                ))}
              </div>
              <div style={{fontSize:'11px',color:'var(--text-muted)',marginTop:'4px'}}>💡 Búscalos en el campo "Intereses y comportamientos" al crear el conjunto</div>
            </div>
          )}

          {/* Recomendaciones */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {aiRecommendations.map((rec, i) => (
              <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <span style={{ background: '#7c3aed', color: '#fff', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', flexShrink: 0, marginTop: '1px' }}>{i+1}</span>
                <span style={{ fontSize: '12px', color: '#e2d9f3', lineHeight: '1.5' }}>{rec}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Texto principal */}
      <div>
        <label style={labelStyle}>Texto principal</label>
        <textarea style={{...inputStyle, minHeight: '70px', resize: 'vertical'}} value={form.body} onChange={e => setForm(f => ({...f, body: e.target.value}))} placeholder="Ej: Transforma tu negocio con nuestra herramienta de IA. Prueba gratis 14 días." />
      </div>

      {/* Titular y descripción */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <label style={labelStyle}>Titular</label>
          <input style={inputStyle} value={form.headline} onChange={e => setForm(f => ({...f, headline: e.target.value}))} placeholder="Ej: Automatiza tu marketing con IA" />
        </div>
        <div>
          <label style={labelStyle}>Descripción</label>
          <input style={inputStyle} value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Ej: Sin tarjeta de crédito. Cancela cuando quieras." />
        </div>
      </div>

      {/* URL destino */}
      <div>
        <label style={labelStyle}>URL destino <span style={{color:'#ef4444',fontSize:'11px'}}>*</span></label>
        <input style={inputStyle} value={form.destination_url} onChange={e => setForm(f => ({...f, destination_url: e.target.value}))} placeholder="https://tusitio.com" />
      </div>

      {/* CTA */}
      <div>
        <label style={labelStyle}>Llamada a la acción</label>
        <select style={inputStyle} value={form.call_to_action} onChange={e => setForm(f => ({...f, call_to_action: e.target.value}))}>
          {CTAS.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
        </select>
      </div>

      {/* Vista previa */}
      <div>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'8px'}}>
          <label style={labelStyle}>Vista previa</label>
          <div style={{display:'flex',gap:'6px'}}>
            {form.publisher_platforms.map(p => (
              <button type="button" key={p} onClick={() => {setPreviewPlatform(p); setShowPreview(true);}} style={{
                padding:'4px 10px',borderRadius:'6px',fontSize:'11px',cursor:'pointer',fontFamily:'var(--font-body)',
                background: previewPlatform===p && showPreview ? '#7c3aed20' : 'var(--bg-elevated)',
                border:`1px solid ${previewPlatform===p && showPreview ? '#7c3aed' : 'var(--border)'}`,
                color: previewPlatform===p && showPreview ? '#7c3aed' : 'var(--text-muted)',
              }}>{p}</button>
            ))}
          </div>
        </div>
        {showPreview && (
          <div style={{border:'1px solid var(--border)',borderRadius:'12px',overflow:'hidden',maxWidth:'340px',margin:'0 auto',background:'#fff'}}>
            {/* Header simulado */}
            <div style={{padding:'10px 12px',display:'flex',alignItems:'center',gap:'8px',borderBottom:'1px solid #eee'}}>
              <div style={{width:'32px',height:'32px',borderRadius:'50%',background:'#7c3aed',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:'14px',fontWeight:'700'}}>T</div>
              <div>
                <div style={{fontSize:'13px',fontWeight:'700',color:'#000'}}>Thitanx</div>
                <div style={{fontSize:'11px',color:'#888'}}>Publicidad · {previewPlatform === 'instagram' ? '📷 Instagram' : previewPlatform === 'messenger' ? '💬 Messenger' : '𝐟 Facebook'}</div>
              </div>
            </div>
            {/* Texto */}
            {form.body && <div style={{padding:'10px 12px',fontSize:'13px',color:'#000',lineHeight:'1.4'}}>{form.body}</div>}
            {/* Imagen */}
            {form.image_url ? (
              <img src={form.image_url} alt="preview" style={{width:'100%',maxHeight:'200px',objectFit:'cover',display:'block'}} onError={e => e.target.style.display='none'} />
            ) : (
              <div style={{width:'100%',height:'160px',background:'#f0f0f0',display:'flex',alignItems:'center',justifyContent:'center',color:'#999',fontSize:'13px'}}>Sin imagen</div>
            )}
            {/* Footer */}
            <div style={{padding:'10px 12px',borderTop:'1px solid #eee',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                {form.headline && <div style={{fontSize:'13px',fontWeight:'700',color:'#000'}}>{form.headline}</div>}
                {form.destination_url && <div style={{fontSize:'11px',color:'#888'}}>{form.destination_url.replace(/https?:\/\//,'')}</div>}
                {form.description && <div style={{fontSize:'11px',color:'#555'}}>{form.description}</div>}
              </div>
              <div style={{padding:'6px 12px',background:'#e4e6ea',borderRadius:'6px',fontSize:'12px',fontWeight:'600',color:'#000',whiteSpace:'nowrap'}}>{(form.call_to_action||'LEARN MORE').replace(/_/g,' ')}</div>
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
        <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
        <button
          disabled={saving || uploading}
          onClick={() => { if(validate()) onSave({...form, media_url: form.image_url, media_type: form.ad_type}); }}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '8px 18px', borderRadius: '8px', border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
            background: saving ? '#7c3aed90' : '#7c3aed', color: '#fff', fontSize: '13px', fontWeight: '700',
            fontFamily: 'var(--font-body)', transition: 'all 0.2s',
          }}
        >
          {saving ? (
            <>
              <span style={{ width: '14px', height: '14px', border: '2px solid #ffffff50', borderTop: '2px solid #fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
              Publicando en Meta...
            </>
          ) : '🚀 Crear anuncio'}
        </button>
      </div>
    </div>
  );
}



// ── Edit AdSet Form ───────────────────────────────────────
function EditAdSetForm({ adset, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    name: adset.name || '',
    budget_amount: adset.budget_amount ?? 0,
    budget_type: adset.budget_type || 'DAILY',
    age_min: adset.age_min || 18,
    age_max: adset.age_max || 65,
    countries: adset.countries || ['CO'],
    genders: adset.genders || [],
    interests: adset.interests || [],
    publisher_platforms: adset.publisher_platforms || ['facebook', 'instagram'],
    messenger_positions: adset.messenger_positions || ['messenger_inbox'],
    audience_network_positions: adset.audience_network_positions || ['classic'],
    facebook_positions: adset.placements?.facebook || ['feed', 'story'],
    instagram_positions: adset.placements?.instagram || ['stream', 'story', 'reels'],
  });
  const COUNTRIES = { CO: '🇨🇴 Colombia', MX: '🇲🇽 México', ES: '🇪🇸 España', AR: '🇦🇷 Argentina', US: '🇺🇸 EE.UU', CL: '🇨🇱 Chile', PE: '🇵🇪 Perú', VE: '🇻🇪 Venezuela' };
  const PLATFORMS = [{id:'facebook',label:'Facebook',color:'#1877F2',icon:'𝐟'},{id:'instagram',label:'Instagram',color:'#E1306C',icon:'📷'},{id:'audience_network',label:'Audience Network',color:'#1877F2',icon:'🌐'},{id:'messenger',label:'Messenger',color:'#00B2FF',icon:'💬'},{id:'whatsapp',label:'WhatsApp',color:'#25D366',icon:'📱'},{id:'threads',label:'Threads',color:'#000',icon:'🧵'}];
  const FB_POSITIONS = ['feed','story','marketplace','right_hand_column','facebook_reels','instream_video'];
  const IG_POSITIONS = ['stream','story','reels','explore','explore_home','ig_search'];
  const MSG_POSITIONS = ['messenger_inbox','messenger_stories'];
  const AN_POSITIONS = ['classic','instream_video','rewarded_video'];
  const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '14px', fontFamily: 'var(--font-body)', outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' };
  const togglePlatform = (id) => setForm(f => {
    const current = f.publisher_platforms;
    let platforms = current.includes(id) ? current.filter(p => p !== id) : [...current, id];
    let ig_pos = f.instagram_positions || [];
    if (id === 'whatsapp' && !current.includes('whatsapp')) {
      if (!platforms.includes('instagram')) platforms = [...platforms, 'instagram'];
      if (!ig_pos.includes('story')) ig_pos = [...ig_pos, 'story'];
    }
    if (id === 'threads' && !current.includes('threads')) {
      if (!platforms.includes('instagram')) platforms = [...platforms, 'instagram'];
      if (!ig_pos.includes('stream')) ig_pos = [...ig_pos, 'stream'];
    }
    if (id === 'audience_network' && !current.includes('audience_network')) {
      if (!platforms.includes('facebook')) platforms = [...platforms, 'facebook'];
      const fb_pos = f.facebook_positions || [];
      if (!fb_pos.includes('feed')) return {...f, publisher_platforms: platforms, instagram_positions: ig_pos, facebook_positions: [...fb_pos, 'feed']};
    }
    return {...f, publisher_platforms: platforms, instagram_positions: ig_pos};
  });
  const toggleFbPos = (pos) => setForm(f => ({...f, facebook_positions: f.facebook_positions.includes(pos) ? f.facebook_positions.filter(p=>p!==pos) : [...f.facebook_positions,pos]}));
  const toggleIgPos = (pos) => setForm(f => ({...f, instagram_positions: f.instagram_positions.includes(pos) ? f.instagram_positions.filter(p=>p!==pos) : [...f.instagram_positions,pos]}));

  const toggleCountry = (code) => setForm(f => ({
    ...f, countries: f.countries.includes(code) ? f.countries.filter(c => c !== code) : [...f.countries, code]
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div>
        <label style={labelStyle}>Nombre del conjunto</label>
        <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          {adset.budget_level === 'CAMPAIGN'
            ? <><label style={labelStyle}>Presupuesto <span style={{color:'var(--text-muted)',fontSize:'11px'}}>(definido en campaña)</span></label><input style={{...inputStyle, opacity:0.6, cursor:'not-allowed'}} type="number" value={form.budget_amount} readOnly disabled /></>
            : <><label style={labelStyle}>Presupuesto USD <span style={{color:'#f59e0b',fontSize:'11px'}}>mín. $1</span></label><input style={inputStyle} type="number" min="1" step="0.01" value={form.budget_amount} onChange={e => setForm(f => ({...f, budget_amount: parseFloat(e.target.value)}))} /></>
          }
        </div>
        <div>
          <label style={labelStyle}>Tipo</label>
          <select style={inputStyle} value={form.budget_type} onChange={e => setForm(f => ({...f, budget_type: e.target.value}))}>
            <option value="DAILY">Diario</option><option value="LIFETIME">Total campaña</option>
          </select>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div><label style={labelStyle}>Edad mínima</label><input style={inputStyle} type="number" min="13" max="65" value={form.age_min} onChange={e => setForm(f => ({...f, age_min: parseInt(e.target.value)}))} /></div>
        <div><label style={labelStyle}>Edad máxima</label><input style={inputStyle} type="number" min="13" max="65" value={form.age_max} onChange={e => setForm(f => ({...f, age_max: parseInt(e.target.value)}))} /></div>
      </div>
      <div>
        <label style={labelStyle}>Países objetivo</label>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {Object.entries(COUNTRIES).map(([code, label]) => (
            <button type="button" key={code} onClick={() => toggleCountry(code)} style={{
              padding: '5px 10px', borderRadius: '7px', fontSize: '12px', cursor: 'pointer',
              background: form.countries.includes(code) ? '#7c3aed20' : 'var(--bg-elevated)',
              border: `1px solid ${form.countries.includes(code) ? '#7c3aed' : 'var(--border)'}`,
              color: form.countries.includes(code) ? '#7c3aed' : 'var(--text-muted)',
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* Redes / Placements */}
      <div>
        <label style={labelStyle}>Redes sociales</label>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
          {PLATFORMS.map(p => (
            <button type="button" key={p.id} onClick={() => togglePlatform(p.id)} style={{
              display:'flex',alignItems:'center',gap:'6px',padding:'8px 14px',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontFamily:'var(--font-body)',
              background: form.publisher_platforms.includes(p.id) ? `${p.color}20` : 'var(--bg-elevated)',
              border: `1px solid ${form.publisher_platforms.includes(p.id) ? p.color : 'var(--border)'}`,
              color: form.publisher_platforms.includes(p.id) ? p.color : 'var(--text-muted)',
            }}><span style={{fontSize:'16px'}}>{p.icon}</span>{p.label}</button>
          ))}
        </div>
        {form.publisher_platforms.includes('facebook') && (
          <div style={{marginBottom:'8px'}}>
            <div style={{fontSize:'12px',color:'#1877F2',fontWeight:'600',marginBottom:'4px'}}>𝐟 Ubicaciones Facebook</div>
            <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
              {FB_POSITIONS.map(pos => (
                <button type="button" key={pos} onClick={() => toggleFbPos(pos)} style={{
                  padding:'4px 8px',borderRadius:'6px',fontSize:'11px',cursor:'pointer',
                  background:form.facebook_positions.includes(pos)?'#1877F220':'var(--bg-elevated)',
                  border:`1px solid ${form.facebook_positions.includes(pos)?'#1877F2':'var(--border)'}`,
                  color:form.facebook_positions.includes(pos)?'#1877F2':'var(--text-muted)',
                }}>{pos}</button>
              ))}
            </div>
          </div>
        )}
        {form.publisher_platforms.includes('instagram') && (
          <div>
            <div style={{fontSize:'12px',color:'#E1306C',fontWeight:'600',marginBottom:'4px'}}>📷 Ubicaciones Instagram</div>
            <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
              {IG_POSITIONS.map(pos => (
                <button type="button" key={pos} onClick={() => toggleIgPos(pos)} style={{
                  padding:'4px 8px',borderRadius:'6px',fontSize:'11px',cursor:'pointer',
                  background:form.instagram_positions.includes(pos)?'#E1306C20':'var(--bg-elevated)',
                  border:`1px solid ${form.instagram_positions.includes(pos)?'#E1306C':'var(--border)'}`,
                  color:form.instagram_positions.includes(pos)?'#E1306C':'var(--text-muted)',
                }}>{pos}</button>
              ))}
            </div>
          </div>
        )}
        {form.publisher_platforms.includes('messenger') && (
          <div style={{marginBottom:'8px'}}>
            <div style={{fontSize:'12px',color:'#00B2FF',fontWeight:'600',marginBottom:'4px'}}>💬 Ubicaciones Messenger</div>
            <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
              {MSG_POSITIONS.map(pos => (
                <button type="button" key={pos} onClick={() => setForm(f => ({...f, messenger_positions: (f.messenger_positions||[]).includes(pos) ? (f.messenger_positions||[]).filter(p=>p!==pos) : [...(f.messenger_positions||[]),pos]}))} style={{
                  padding:'4px 8px',borderRadius:'6px',fontSize:'11px',cursor:'pointer',
                  background:(form.messenger_positions||[]).includes(pos)?'#00B2FF20':'var(--bg-elevated)',
                  border:`1px solid ${(form.messenger_positions||[]).includes(pos)?'#00B2FF':'var(--border)'}`,
                  color:(form.messenger_positions||[]).includes(pos)?'#00B2FF':'var(--text-muted)',
                }}>{pos}</button>
              ))}
            </div>
          </div>
        )}
        {form.publisher_platforms.includes('audience_network') && (
          <div style={{marginBottom:'8px'}}>
            <div style={{fontSize:'12px',color:'#1877F2',fontWeight:'600',marginBottom:'4px'}}>🌐 Ubicaciones Audience Network</div>
            <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
              {AN_POSITIONS.map(pos => (
                <button type="button" key={pos} onClick={() => setForm(f => ({...f, audience_network_positions: (f.audience_network_positions||[]).includes(pos) ? (f.audience_network_positions||[]).filter(p=>p!==pos) : [...(f.audience_network_positions||[]),pos]}))} style={{
                  padding:'4px 8px',borderRadius:'6px',fontSize:'11px',cursor:'pointer',
                  background:(form.audience_network_positions||[]).includes(pos)?'#1877F220':'var(--bg-elevated)',
                  border:`1px solid ${(form.audience_network_positions||[]).includes(pos)?'#1877F2':'var(--border)'}`,
                  color:(form.audience_network_positions||[]).includes(pos)?'#1877F2':'var(--text-muted)',
                }}>{pos}</button>
              ))}
            </div>
          </div>
        )}
        {form.publisher_platforms.includes('whatsapp') && (
          <div style={{marginBottom:'8px',padding:'8px 12px',background:'#25D36615',borderRadius:'8px',border:'1px solid #25D36630'}}>
            <div style={{fontSize:'12px',color:'#25D366',fontWeight:'600'}}>📱 WhatsApp — Estado de WhatsApp (requiere Instagram Stories)</div>
          </div>
        )}
        {form.publisher_platforms.includes('threads') && (
          <div style={{marginBottom:'8px',padding:'8px 12px',background:'#00000015',borderRadius:'8px',border:'1px solid #00000030'}}>
            <div style={{fontSize:'12px',color:'var(--text-primary)',fontWeight:'600'}}>🧵 Threads — Feed de Threads (requiere Instagram Feed)</div>
          </div>
        )}
      </div>
      {/* Géneros */}
      <div>
        <label style={labelStyle}>Género objetivo</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[{id:0,label:'Todos'},{id:1,label:'Hombres'},{id:2,label:'Mujeres'}].map(g => (
            <button type="button" key={g.id} onClick={() => setForm(f => ({...f, genders: g.id === 0 ? [] : [g.id]}))} style={{
              padding:'7px 14px', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontFamily:'var(--font-body)',
              background: (g.id === 0 ? (!form.genders||form.genders.length===0) : form.genders?.includes(g.id)) ? '#7c3aed20' : 'var(--bg-elevated)',
              border: `1px solid ${(g.id === 0 ? (!form.genders||form.genders.length===0) : form.genders?.includes(g.id)) ? '#7c3aed' : 'var(--border)'}`,
              color: (g.id === 0 ? (!form.genders||form.genders.length===0) : form.genders?.includes(g.id)) ? '#7c3aed' : 'var(--text-muted)',
            }}>{g.label}</button>
          ))}
        </div>
      </div>
      {/* Intereses */}
      <div>
        <label style={labelStyle}>Intereses y comportamientos <span style={{fontSize:'11px',color:'var(--text-muted)',fontWeight:'400'}}>(opcional pero recomendado)</span></label>
        <InterestSearch interests={form.interests||[]} onChange={v => setForm(f => ({...f, interests: v}))} />
      </div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
        <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
        <Button size="sm" loading={saving} onClick={() => { if(form.publisher_platforms.length===0){toast.error("Selecciona al menos una plataforma");return;} onSave(form); }}>Guardar cambios</Button>
      </div>
    </div>
  );
}

// ── Edit Ad Form ──────────────────────────────────────────
function EditAdForm({ ad, adAccountId, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    name: ad.name || '',
    ad_type: ad.media_type || 'image',
    headline: ad.headline || '',
    body: ad.body || '',
    description: ad.description || '',
    destination_url: ad.destination_url || '',
    image_url: ad.media_url || '',
    call_to_action: ad.call_to_action || 'LEARN_MORE',
    video_id: ad.video_id || '',
    thumbnail_url: ad.thumbnail_url || '',
    thumbnail_file: ad.thumbnail_url ? ad.thumbnail_url.split('/').pop() : '',
    publisher_platforms: ad.publisher_platforms || ['facebook', 'instagram'],
    placements: ad.placements || { facebook: ['feed','story'], instagram: ['stream','story'] },
  });
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '14px', fontFamily: 'var(--font-body)', outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' };
  const CTAS = [{v:'LEARN_MORE',l:'Más información'},{v:'SHOP_NOW',l:'Comprar ahora'},{v:'SIGN_UP',l:'Registrarse'},{v:'CONTACT_US',l:'Contáctenos'},{v:'BOOK_TRAVEL',l:'Reservar viaje'},{v:'DOWNLOAD',l:'Descargar'},{v:'GET_OFFER',l:'Ver oferta'},{v:'WATCH_MORE',l:'Ver más'},{v:'SUBSCRIBE',l:'Suscribirse'},{v:'GET_QUOTE',l:'Obtener cotización'},{v:'SEND_MESSAGE',l:'Enviar mensaje'}];

  const handleImageUpload = async (file) => {
    if (!file) return;
    try { await validateImageDimensions(file); } catch(err) { toast.error(err); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { default: api } = await import('../api/client');
      const { data } = await api.post('/posts/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = data.url || data.media_url || data.path || (data.media_id ? `${window.location.origin}/uploads/${data.media_id}` : null);
      setForm(f => ({ ...f, image_url: url }));
      setImageFile(file);
    } catch (e) { toast.error('Error subiendo imagen'); }
    setUploading(false);
  };

  const validate = () => {
    if (!form.name.trim()) { toast.error('El nombre es requerido'); return false; }
    if (!form.destination_url.trim()) { toast.error('La URL destino es requerida'); return false; }
    try { const u = new URL(form.destination_url); if (!['http:','https:'].includes(u.protocol)) throw new Error(); const parts = u.hostname.split('.'); if (parts.length < 2 || parts[parts.length-1].length < 2 || !/^[a-zA-Z]+$/.test(parts[parts.length-1])) throw new Error(); } catch(e) { toast.error('La URL destino no es valida. Ejemplo: https://tusitio.com'); return false; }
    if (form.ad_type === 'video' && !form.video_id) { toast.error('Debes subir y esperar que el video esté listo'); return false; }
    if (form.ad_type === 'video' && !form.thumbnail_url) { toast.error('La miniatura del video es requerida'); return false; }
    return true;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div>
        <label style={labelStyle}>Tipo de anuncio</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[{id:'image',label:'🖼 Imagen'},{id:'video',label:'🎬 Video'}].map(t => (
            <button type="button" key={t.id} onClick={() => setForm(f => ({...f, ad_type: t.id}))} style={{
              padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '13px',
              background: form.ad_type === t.id ? '#7c3aed20' : 'var(--bg-elevated)',
              border: `1px solid ${form.ad_type === t.id ? '#7c3aed' : 'var(--border)'}`,
              color: form.ad_type === t.id ? '#7c3aed' : 'var(--text-muted)',
            }}>{t.label}</button>
          ))}
        </div>
      </div>
      <div>
        <label style={labelStyle}>Nombre del anuncio <span style={{color:'#ef4444',fontSize:'11px'}}>*</span></label>
        <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} />
      </div>
      {form.ad_type === 'image' ? (
        <div>
          <label style={labelStyle}>Imagen</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '8px', border: '2px dashed var(--border)', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '13px' }}>
              <span>📁</span>
              <span>{imageFile ? imageFile.name : uploading ? 'Subiendo...' : 'Cambiar imagen'}</span>
              <input type="file" accept="image/*" style={{display:'none'}} onChange={e => handleImageUpload(e.target.files[0])} />
            </label>
            {form.image_url && <img src={form.image_url} alt="preview" style={{maxHeight:'100px',borderRadius:'8px',objectFit:'cover'}} onError={e => e.target.style.display='none'} />}
            <input style={inputStyle} value={form.image_url} onChange={e => setForm(f => ({...f, image_url: e.target.value}))} placeholder="https://tusitio.com/imagen.jpg" />
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div>
            <label style={labelStyle}>Video</label>
            <VideoUploader adAccountId={adAccountId} value={form.video_id} onChange={v => setForm(f => ({...f, video_id: v}))} />
          </div>
          <div>
            <label style={labelStyle}>Miniatura del video <span style={{color:'#ef4444',fontSize:'11px'}}>*</span></label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '8px', border: '2px dashed var(--border)', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '13px' }}>
              <span>🖼</span>
              <span>{form.thumbnail_file || 'Seleccionar miniatura (JPG, PNG — mín. 500px ancho)'}</span>
              <input type="file" accept="image/*" style={{display:'none'}} onChange={async e => {
                const file = e.target.files[0];
                if (!file) return;
                try { await validateImageDimensions(file); } catch(err) { toast.error(err); return; }
                const fd = new FormData();
                fd.append('file', file);
                const { default: api } = await import('../api/client');
                const { data } = await api.post('/posts/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                const url = data.url || data.media_url || data.path || (data.media_id ? `/uploads/${data.media_id}` : null);
                setForm(f => ({ ...f, thumbnail_url: url, thumbnail_file: file.name }));
              }} />
            </label>
            <div style={{fontSize:'11px',color:'var(--text-muted)',marginTop:'4px'}}>JPG o PNG · Mín. 500px ancho · Recomendado 1200x628px · Relación 1.91:1 o 1:1</div>
            {form.thumbnail_url && <div style={{fontSize:'11px',color:'#10b981',marginTop:'4px'}}>✅ Miniatura: {form.thumbnail_url.split('/').pop()}</div>}
          </div>
        </div>
      )}
      <div>
        <label style={labelStyle}>Texto principal</label>
        <textarea style={{...inputStyle, minHeight: '70px', resize: 'vertical'}} value={form.body} onChange={e => setForm(f => ({...f, body: e.target.value}))} placeholder="Ej: Transforma tu negocio con nuestra herramienta de IA. Prueba gratis 14 días." />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <label style={labelStyle}>Titular</label>
          <input style={inputStyle} value={form.headline} onChange={e => setForm(f => ({...f, headline: e.target.value}))} placeholder="Ej: Automatiza tu marketing con IA" />
        </div>
        <div>
          <label style={labelStyle}>Descripción</label>
          <input style={inputStyle} value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Ej: Sin tarjeta de crédito. Cancela cuando quieras." />
        </div>
      </div>
      <div>
        <label style={labelStyle}>URL destino <span style={{color:'#ef4444',fontSize:'11px'}}>*</span></label>
        <input style={inputStyle} value={form.destination_url} onChange={e => setForm(f => ({...f, destination_url: e.target.value}))} placeholder="https://tusitio.com" />
      </div>
      <div>
        <label style={labelStyle}>Llamada a la acción</label>
        <select style={inputStyle} value={form.call_to_action} onChange={e => setForm(f => ({...f, call_to_action: e.target.value}))}>
          {CTAS.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
        </select>
      </div>

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
        <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
        <button
          disabled={saving || uploading}
          onClick={() => { if(validate()) onSave({...form, media_type: form.ad_type, thumbnail_url: form.ad_type==='image' ? '' : form.thumbnail_url, video_id: form.ad_type==='image' ? '' : form.video_id, image_url: form.ad_type==='video' ? '' : form.image_url}); }}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '8px 18px', borderRadius: '8px', border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
            background: saving ? '#7c3aed90' : '#7c3aed', color: '#fff', fontSize: '13px', fontWeight: '700',
            fontFamily: 'var(--font-body)', transition: 'all 0.2s',
          }}
        >
          {saving ? (
            <>
              <span style={{ width: '14px', height: '14px', border: '2px solid #ffffff50', borderTop: '2px solid #fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
              Actualizando en Meta...
            </>
          ) : '💾 Guardar cambios'}
        </button>
      </div>
    </div>
  );
}


// ── Ad Previews Modal ─────────────────────────────────────
function AdPreviewsModal({ ad, onClose, onEdit }) {
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const FORMAT_LABELS = {
    DESKTOP_FEED_STANDARD:  { label: 'Facebook Feed (escritorio)', icon: '𝐟', color: '#1877F2' },
    MOBILE_FEED_STANDARD:   { label: 'Facebook Feed (móvil)',      icon: '𝐟', color: '#1877F2' },
    INSTAGRAM_STANDARD:     { label: 'Instagram Feed',             icon: '📷', color: '#E1306C' },
    INSTAGRAM_STORY:        { label: 'Instagram Story',            icon: '📷', color: '#E1306C' },
    FACEBOOK_STORY_MOBILE:            { label: 'Facebook Story',             icon: '𝐟', color: '#1877F2' },
    FACEBOOK_REELS_MOBILE:            { label: 'Facebook Reels',             icon: '𝐟', color: '#1877F2' },
    INSTAGRAM_REELS:                  { label: 'Instagram Reels',            icon: '📷', color: '#E1306C' },
    MARKETPLACE_MOBILE:               { label: 'Marketplace',                icon: '🛒', color: '#1877F2' },
    MESSENGER_MOBILE_INBOX_MEDIA:     { label: 'Messenger Bandeja',          icon: '💬', color: '#00B2FF' },
    MESSENGER_MOBILE_STORY_MEDIA:     { label: 'Messenger Story',            icon: '💬', color: '#00B2FF' },
    AUDIENCE_NETWORK_OUTSTREAM_VIDEO: { label: 'Audience Network Video',     icon: '🌐', color: '#1877F2' },
    SUGGESTED_VIDEO_MOBILE:           { label: 'Video sugerido (móvil)',     icon: '▶️', color: '#888'    },
    RIGHT_COLUMN_STANDARD:  { label: 'Columna derecha',            icon: '𝐟', color: '#1877F2' },
  };

  useEffect(() => {
    const fbPos  = ad.facebook_positions  || [];
    const igPos  = ad.instagram_positions || [];
    const platforms = ad.publisher_platforms || [];

    const FORMAT_MAP = {
      DESKTOP_FEED_STANDARD:            { plat: 'facebook', pos: 'feed' },
      MOBILE_FEED_STANDARD:             { plat: 'facebook', pos: 'feed' },
      FACEBOOK_STORY_MOBILE:            { plat: 'facebook', pos: 'story' },
      FACEBOOK_REELS_MOBILE:            { plat: 'facebook', pos: 'reels' },
      MARKETPLACE_MOBILE:               { plat: 'facebook', pos: 'marketplace' },
      RIGHT_COLUMN_STANDARD:            { plat: 'facebook', pos: 'right_hand_column' },
      INSTAGRAM_STANDARD:               { plat: 'instagram', pos: 'stream' },
      INSTAGRAM_STORY:                  { plat: 'instagram', pos: 'story' },
      INSTAGRAM_REELS:                  { plat: 'instagram', pos: 'reels' },
    };

    adsAPI.getAdPreviews(ad.id)
      .then(r => {
        const data = r.data.previews || [];
        setPreviews(data);
        if (data.length > 0) setSelected(data[0].format);
      })
      .catch(() => toast.error('Error cargando vistas previas'))
      .finally(() => setLoading(false));
  }, [ad.id]);

  const current = previews.find(p => p.format === selected);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={e => { if(e.target===e.currentTarget) onClose(); }}>
      <div style={{ width: '99vw', maxWidth: '1600px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', height: '96vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexShrink: 0 }}>
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: '800', fontSize: '17px' }}>👁 Vista previa — {ad.name}</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Vista previa real desde Meta Ads</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button size="sm" onClick={() => onEdit(ad)}>✏️ Editar anuncio</Button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '20px' }}>✕</button>
          </div>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, paddingRight: '4px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}>Cargando vistas previas desde Meta...</div>
        ) : previews.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '36px', marginBottom: '8px' }}>📭</div>
            <p>No hay vistas previas disponibles. El anuncio puede estar en proceso de revisión por Meta.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: '1', minHeight: '0' }}>


            {/* Preview grid */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              {previews.map(p => {
                const meta = FORMAT_LABELS[p.format] || { label: p.format, icon: '📱', color: '#888' };
                return (
                  <div key={p.format} style={{ flex: '0 0 280px', maxWidth: '320px', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden', background: '#fff' }}>
                    <div style={{ padding: '8px 12px', background: '#f0f2f5', fontSize: '12px', color: '#555', borderBottom: '1px solid #ddd', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>{meta.icon}</span><span>{meta.label}</span>
                    </div>
                    {(() => {
                      const iframeMatch = p.body.match(/width="(\d+)" height="(\d+)"/);
                      const iw = iframeMatch ? parseInt(iframeMatch[1]) : 540;
                      const ih = iframeMatch ? parseInt(iframeMatch[2]) : 690;
                      const maxW = 300;
                      const scale = maxW / iw;
                      return (
                        <div style={{ width: maxW + 'px', height: Math.round(ih * scale) + 'px', overflow: 'hidden', position: 'relative', margin: '0 auto' }}>
                          <div dangerouslySetInnerHTML={{ __html: p.body }}
                            style={{ position: 'absolute', top: 0, left: 0, width: iw + 'px', height: ih + 'px', transform: `scale(${scale})`, transformOrigin: 'top left', pointerEvents: 'none' }} />
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

// ── AdSets Manager Modal ──────────────────────────────────
function AdSetsModal({ campaign, adAccountId, onClose, autoCreateAdSet }) {
  const [adsets, setAdsets] = useState([]);
  const [ads, setAds] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdSetForm, setShowAdSetForm] = useState(autoCreateAdSet || false);
  const [showAdForm, setShowAdForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedAdSet, setExpandedAdSet] = useState(null);
  const [editingAd, setEditingAd] = useState(null);
  const [editingAdSet, setEditingAdSet] = useState(null);
  const [previewAd, setPreviewAd] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adsAPI.listAdSets(campaign.id);
      setAdsets(r.data);
    } catch (e) { toast.error('Error cargando conjuntos'); }
    setLoading(false);
  };

  const [adsetInsights, setAdsetInsights] = useState({});
  const [adInsights, setAdInsights] = useState({});

  const loadAds = async (adsetId) => {
    try {
      const r = await adsAPI.listAds(adsetId);
      setAds(prev => ({ ...prev, [adsetId]: r.data }));
      // Load adset insights
      try {
        const ri = await adsAPI.getAdSetInsights(adsetId);
        setAdsetInsights(prev => ({ ...prev, [adsetId]: ri.data }));
      } catch (e) {}
    } catch (e) {}
  };

  const loadAdInsights = async (adId) => {
    try {
      const r = await adsAPI.getAdInsights(adId);
      setAdInsights(prev => ({ ...prev, [adId]: r.data }));
    } catch (e) {}
  };

  const handleAdSetEdit = async (id, form) => {
    setSaving(true);
    try {
      await adsAPI.updateAdSet(id, form);
      toast.success('Conjunto actualizado ✓');
      setEditingAdSet(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al actualizar'); }
    setSaving(false);
  };

  const handleAdSetStatus = async (id, status) => {
    try {
      await adsAPI.updateAdSetStatus(id, status);
      toast.success(status === 'ACTIVE' ? 'Conjunto activado ✓' : 'Conjunto pausado ✓');
      await load();
    } catch (e) { toast.error('Error al actualizar estado'); }
  };

  const handleAdSetDelete = async (id) => {
    showConfirm('Eliminar conjunto', '¿Estás seguro? Esta acción eliminará el conjunto y sus anuncios permanentemente.', 'Eliminar', async () => {
      setConfirmDialog(null);
      try {
        await adsAPI.deleteAdSet(id);
        toast.success('Conjunto eliminado ✓');
        await load();
      } catch (e) { toast.error('Error al eliminar'); }
    });
  };

  const handleAdStatus = async (adId, adsetId, status) => {
    try {
      await adsAPI.updateAdStatus(adId, status);
      toast.success(status === 'ACTIVE' ? 'Anuncio activado ✓' : 'Anuncio pausado ✓');
      await loadAds(adsetId);
    } catch (e) { toast.error('Error al actualizar estado'); }
  };

  const handleAdEdit = async (adId, form) => {
    setSaving(true);
    try {
      await adsAPI.updateAd(adId, form);
      toast.success('Anuncio actualizado ✓');
      setEditingAd(null);
      const adsetId = adsets.find(a => ads[a.id]?.find(ad => ad.id === adId))?.id;
      if (adsetId) await loadAds(adsetId);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al actualizar'); }
    setSaving(false);
  };

  const handleAdDelete = async (adId, adsetId) => {
    showConfirm('Eliminar anuncio', '¿Estás seguro? Esta acción eliminará el anuncio permanentemente.', 'Eliminar', async () => {
      setConfirmDialog(null);
      try {
        await adsAPI.deleteAd(adId);
        toast.success('Anuncio eliminado ✓');
        await loadAds(adsetId);
      } catch (e) { toast.error('Error al eliminar'); }
    });
  };

  useEffect(() => { load(); }, []);

  const handleAdSetSave = async (form) => {
    setSaving(true);
    try {
      const r = await adsAPI.createAdSet(campaign.id, form);
      toast.success('Conjunto creado ✓');
      setShowAdSetForm(false);
      await load();
      if (r?.data?.id) setExpandedAdSet(r.data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al crear conjunto'); }
    setSaving(false);
  };

  const handleAdSave = async (form) => {
    setSaving(true);
    try {
      const payload = {...form, media_url: form.image_url, media_type: form.ad_type, thumbnail_url: form.thumbnail_url};
      await adsAPI.createAd(payload);
      toast.success('Anuncio creado ✓');
      const adsetId = form.ad_set_id || form.adset_id;
      setShowAdForm(null);
      setExpandedAdSet(adsetId);
      await loadAds(adsetId);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al crear anuncio'); }
    setSaving(false);
  };

  const toggleAdSet = async (adsetId) => {
    if (expandedAdSet === adsetId) { setExpandedAdSet(null); return; }
    setExpandedAdSet(adsetId);
    if (!ads[adsetId]) await loadAds(adsetId);
  };

  const [metaTab, setMetaTab] = useState('adsets');
  const [confirmDialog, setConfirmDialog] = useState(null);
  const showConfirm = (title, message, confirmLabel, onConfirm) => setConfirmDialog({ title, message, confirmLabel, onConfirm });

  const tbStyle = { fontSize: '13px', color: 'var(--text-muted)', padding: '10px 14px', whiteSpace: 'nowrap', fontWeight: '600', textAlign: 'left', borderBottom: '2px solid var(--border)', background: 'var(--bg-elevated)' };
  const tdStyle = { fontSize: '13px', padding: '10px 14px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle' };

  const ToggleSwitch = ({ active, onChange }) => (
    <div onClick={onChange} style={{ width: '36px', height: '20px', borderRadius: '10px', background: active ? '#1877F2' : '#ccc', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: '2px', left: active ? '18px' : '2px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
    </div>
  );

  const DeliveryBadge = ({ status }) => {
    const map = { ACTIVE: { label: 'Activo', color: '#10b981' }, PAUSED: { label: 'Pausado', color: '#888' }, DELETED: { label: 'Eliminado', color: '#ef4444' }, ARCHIVED: { label: 'Archivado', color: '#f59e0b' } };
    const s = map[status] || { label: status, color: '#888' };
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: s.color, fontWeight: '600' }}><span style={{ width: '8px', height: '8px', borderRadius: '50%', background: s.color, display: 'inline-block' }} />{s.label}</span>;
  };

  const allAds = Object.values(ads).flat();

  return (
    <>
    {confirmDialog && <ConfirmDialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)} onConfirm={confirmDialog.onConfirm} title={confirmDialog.title} message={confirmDialog.message} confirmLabel={confirmDialog.confirmLabel} danger />}
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 299, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={(e) => { if(e.target===e.currentTarget) onClose(); }}>
      <div style={{ width: 'min(960px, 97vw)', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

        {/* Forms overlay */}
        {(showAdSetForm || previewAd || editingAdSet || editingAd || showAdForm) && (
          <div style={{ padding: '24px' }}>
            {showAdSetForm && (<><h3 style={{ fontFamily: 'var(--font-display)', fontWeight: '700', fontSize: '15px', marginBottom: '16px' }}>Nuevo conjunto de anuncios</h3><CreateAdSetForm campaignId={campaign.id} adAccountId={adAccountId} budgetLevel={campaign.budget_level || "CAMPAIGN"} onSave={handleAdSetSave} onCancel={() => setShowAdSetForm(false)} saving={saving} campaign={campaign} /></>)}
            {previewAd && <AdPreviewsModal ad={previewAd} onClose={() => setPreviewAd(null)} onEdit={(ad) => { setPreviewAd(null); setEditingAd(ad); }} />}
            {editingAdSet && (<><h3 style={{ fontFamily: 'var(--font-display)', fontWeight: '700', fontSize: '15px', marginBottom: '16px' }}>✏️ Editar conjunto</h3><EditAdSetForm adset={editingAdSet} onSave={(form) => handleAdSetEdit(editingAdSet.id, form)} onCancel={() => setEditingAdSet(null)} saving={saving} /></>)}
            {editingAd && (<><h3 style={{ fontFamily: 'var(--font-display)', fontWeight: '700', fontSize: '15px', marginBottom: '16px' }}>✏️ Editar anuncio</h3><EditAdForm ad={editingAd} adAccountId={adAccountId} onSave={(form) => handleAdEdit(editingAd.id, form)} onCancel={() => setEditingAd(null)} saving={saving} /></>)}
            {showAdForm && (<><h3 style={{ fontFamily: 'var(--font-display)', fontWeight: '700', fontSize: '15px', marginBottom: '16px' }}>Nuevo anuncio</h3><CreateAdForm adSetId={showAdForm} adAccountId={adAccountId} onSave={handleAdSave} onCancel={() => setShowAdForm(null)} saving={saving} campaign={campaign} adset={adsets.find(a => a.id === showAdForm)} pixelId={null} /></>)}
          </div>
        )}

        {!showAdSetForm && !previewAd && !editingAdSet && !editingAd && !showAdForm && (<>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <NetworkIcon network={campaign.network} size={20} />
            <span style={{ fontWeight: '800', fontSize: '16px', fontFamily: 'var(--font-display)' }}>{campaign.name}</span>
            <DeliveryBadge status={campaign.status} />
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '20px', lineHeight: 1 }}>✕</button>
        </div>

        {/* Toolbar */}
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
          <Button size="sm" icon="+" onClick={() => setShowAdSetForm(true)}>+ Nuevo conjunto</Button>
          <div style={{ flex: 1 }} />
          <button onClick={load} style={{ padding: '7px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>↻ Actualizar</button>
        </div>

        {/* Vista jerárquica */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
            <p>Cargando estructura de campaña...</p>
          </div>
        ) : adsets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>🎯</div>
            <p style={{ fontSize: '15px', fontWeight: '600', marginBottom: '8px' }}>Sin conjuntos de anuncios</p>
            <p style={{ fontSize: '13px' }}>Crea el primer conjunto para comenzar</p>
          </div>
        ) : (
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {adsets.map(adset => {
              const ins = adsetInsights[adset.id];
              const expanded = expandedAdSet === adset.id;
              const adsetAds = ads[adset.id] || [];
              return (
                <div key={adset.id} style={{ border: `1px solid ${expanded ? '#7c3aed50' : 'var(--border)'}`, borderRadius: '12px', overflow: 'hidden', background: 'var(--bg-card)', transition: 'border-color 0.2s' }}>
                  {/* Adset row */}
                  <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', background: expanded ? '#7c3aed08' : 'var(--bg-card)', borderBottom: expanded ? '1px solid var(--border)' : 'none' }}>
                    <ToggleSwitch active={adset.status==='ACTIVE'} onChange={() => handleAdSetStatus(adset.id, adset.status==='ACTIVE'?'PAUSED':'ACTIVE')} />
                    <div style={{ cursor: 'pointer', flex: 1, minWidth: 0 }} onClick={() => toggleAdSet(adset.id)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: '700', fontSize: '14px' }}>🎯 {adset.name}</span>
                        <DeliveryBadge status={adset.status} />
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)', background: 'var(--bg-elevated)', padding: '2px 8px', borderRadius: '12px' }}>{fmtCurrency(adset.budget_amount, 'COP')}/día</span>
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>👥 {adset.age_min}–{adset.age_max} años</span><span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>📅 {adset.created_at ? new Date(adset.created_at.replace(' ','T')).toLocaleString('es-CO',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—'}</span>
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px', display: 'flex', gap: '16px' }}>
                        <span>Impresiones: {ins ? fmt(ins.impressions||0) : '—'}</span>
                        <span>Clics: {ins ? fmt(ins.clicks||0) : '—'}</span>
                        <span>Gasto: {ins ? fmtCurrency(ins.spend||0, 'COP') : '—'}</span><span>📅 {adset.created_at ? new Date(adset.created_at.replace(' ','T')).toLocaleString('es-CO',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—'}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                      <button onClick={() => setShowAdForm(adset.id)} style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #7c3aed50', background: '#7c3aed15', color: '#a78bfa', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>+ Anuncio</button>
                      <button onClick={() => setEditingAdSet(adset)} style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '13px' }} title="Editar conjunto">✏️</button>
                      <button onClick={() => handleAdSetDelete(adset.id)} style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #ef444430', background: '#ef444415', color: '#ef4444', cursor: 'pointer', fontSize: '13px' }} title="Eliminar conjunto">🗑</button>
                      <button onClick={() => toggleAdSet(adset.id)} style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '14px', fontWeight: '700' }}>{expanded ? '▲' : '▼'}</button>
                    </div>
                  </div>
                  {/* Ads expandidos */}
                  {expanded && (
                    <div style={{ padding: '12px 16px 16px 32px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {adsetAds.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', background: 'var(--bg-elevated)', borderRadius: '10px' }}>
                          <div style={{ fontSize: '24px', marginBottom: '6px' }}>📢</div>
                          <p style={{ fontSize: '13px' }}>Sin anuncios en este conjunto.</p>
                          <button onClick={() => setShowAdForm(adset.id)} style={{ marginTop: '10px', padding: '7px 16px', borderRadius: '8px', border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>+ Crear anuncio</button>
                        </div>
                      ) : adsetAds.map(ad => {
                        const ins = adInsights[ad.id];
                        console.log('AD DATA:', ad.id, ad.created_at);
                        const thumb = ad.thumbnail_url || ad.media_url;
                        return (
                          <div key={ad.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', borderRadius: '10px', background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}
                            onMouseEnter={() => { if(!adInsights[ad.id] && ad.platform_id) loadAdInsights(ad.id); }}>
                            <ToggleSwitch active={ad.status==='ACTIVE'} onChange={() => handleAdStatus(ad.id, adset.id, ad.status==='ACTIVE'?'PAUSED':'ACTIVE')} />
                            {thumb ? <img src={thumb} alt="" style={{ width: '44px', height: '44px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} onError={e => e.target.style.display='none'} /> : <div style={{ width: '44px', height: '44px', borderRadius: '8px', background: 'var(--bg-card)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>📢</div>}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: '600', fontSize: '13px' }}>{ad.name}</span>
                                <DeliveryBadge status={ad.status} />
                                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{ad.media_type === 'video' ? '🎬 Video' : '🖼 Imagen'}</span>
                              </div>
                              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '3px', display: 'flex', gap: '16px' }}>
                                <span>Impresiones: {ins ? fmt(ins.impressions||0) : '—'}</span>
                                <span>Clics: {ins ? fmt(ins.clicks||0) : '—'}</span>
                                <span>Gasto: {ins ? fmtCurrency(ins.spend||0, 'COP') : '—'}</span><span>📅 {ad.created_at ? new Date(ad.created_at.replace(' ','T')).toLocaleString('es-CO',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—'}</span>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                              <button onClick={() => setPreviewAd({...ad, publisher_platforms: adset.publisher_platforms, facebook_positions: adset.facebook_positions, instagram_positions: adset.instagram_positions})} style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '13px' }} title="Vista previa">👁</button>
                              <button onClick={() => setEditingAd(ad)} style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '13px' }} title="Editar">✏️</button>
                              <button onClick={() => handleAdDelete(ad.id, adset.id)} style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #ef444430', background: '#ef444415', color: '#ef4444', cursor: 'pointer', fontSize: '13px' }} title="Eliminar">🗑</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </>)}
      </div>
    </div>
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────
export default function AdsManagerPage() {
  const [tab, setTab]               = useState('overview');
  const [accounts, setAccounts]     = useState([]);
  const [campaigns, setCampaigns]   = useState([]);
  const [overview, setOverview]     = useState(null);
  const [loading, setLoading]       = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [selected, setSelected]     = useState(null);
  const [deleting, setDeleting]     = useState(null);
  const [syncNet, setSyncNet]       = useState(null);
  const [adSetsFor, setAdSetsFor]   = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [accs, camps, ov] = await Promise.all([
        adsAPI.listAccounts(),
        adsAPI.listCampaigns(),
        adsAPI.overview(),
      ]);
      setAccounts(accs.data);
      setCampaigns(camps.data);
      setOverview(ov.data);
    } catch (e) {
      toast.error('Error cargando datos de campañas');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSync = async (network) => {
    setSyncNet(network);
    try {
      const r = await adsAPI.syncAccounts(network);
      toast.success(`${r.data.total} cuenta(s) sincronizadas de ${network}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Error sincronizando ${network}`);
    }
    setSyncNet(null);
  };

  const handleCreate = async (form) => {
    setSaving(true);
    try {
      form.start_time = form.start_time ? new Date(form.start_time).toISOString() : null;
      form.end_time = form.end_time ? new Date(form.end_time).toISOString() : null;
      await adsAPI.createCampaign(form);
      toast.success('Campaña creada ✓');
      setShowCreate(false);
      await load();
    } catch (e) {
      const _d = e?.response?.data?.detail; toast.error(Array.isArray(_d) ? _d.map(x=>x.msg).join(', ') : (_d || 'Error al crear campaña'));
    }
    setSaving(false);
  };

  const handleStatusChange = async (id, status) => {
    try {
      await adsAPI.updateStatus(id, status);
      toast.success(`Campaña ${status === 'ACTIVE' ? 'activada' : 'pausada'} ✓`);
      setCampaigns(prev => prev.map(c => c.id === id ? { ...c, status } : c));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleDelete = async (campaign) => {
    try {
      await adsAPI.deleteCampaign(campaign.id);
      toast.success('Campaña eliminada');
      setCampaigns(prev => prev.filter(c => c.id !== campaign.id));
      if (selected?.id === campaign.id) setSelected(null);
    } catch (e) {
      toast.error('Error al eliminar');
    }
    setDeleting(null);
  };

  const TABS = [
    { id: 'overview',   label: '📊 Resumen' },
    { id: 'campaigns',  label: '🚀 Campañas' },
    { id: 'adsets',     label: '🎯 Conjuntos' },
    { id: 'ads',        label: '📢 Anuncios' },
    { id: 'insights',   label: '📈 Métricas' },
    { id: 'accounts',   label: '🏦 Cuentas Ad' },
  ];
  const [inlineAdSets, setInlineAdSets] = useState([]);
  const [inlineAds, setInlineAds] = useState({});
  const [inlineLoading, setInlineLoading] = useState(false);
  const [inlineAdSetInsights, setInlineAdSetInsights] = useState({});
  const [editingInlineAdSet, setEditingInlineAdSet] = useState(null);
  const [editingInlineAd, setEditingInlineAd] = useState(null);
  const [previewInlineAd, setPreviewInlineAd] = useState(null);
  const [showInlineAdForm, setShowInlineAdForm] = useState(null);
  const [showCreateAdSetModal, setShowCreateAdSetModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [inlineSaving, setInlineSaving] = useState(false);
  const handleInlineAdEdit = async (id, form) => {
    setInlineSaving(true);
    try {
      await adsAPI.updateAd(id, form);
      toast.success('Anuncio actualizado ✓');
      setEditingInlineAd(null);
      await loadInlineAdSets(selected);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al actualizar'); }
    setInlineSaving(false);
  };
  const handleInlineAdSetEdit = async (id, form) => {
    try {
      await adsAPI.updateAdSet(id, form);
      toast.success('Conjunto actualizado ✓');
      setEditingInlineAdSet(null);
      await loadInlineAdSets(selected);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al actualizar'); }
  };

  const handleCampaignEdit = async (form) => {
    setSavingCampaign(true);
    try {
      await adsAPI.updateCampaign(editingCampaign.id, form);
      toast.success('Campaña actualizada ✓');
      setEditingCampaign(null);
      load();
    } catch(e) { toast.error(e?.response?.data?.detail || 'Error al actualizar'); }
    setSavingCampaign(false);
  };
  const loadInlineAdSets = async (campaign) => {
    setSelected(campaign);
    setInlineLoading(true);
    setInlineAdSets([]);
    setInlineAds({});
    try {
      const r = await adsAPI.listAdSets(campaign.id);
      const adsets = r.data;
      setInlineAdSets(adsets);
      // Cargar ads de todos los conjuntos
      await Promise.all(adsets.map(async a => {
        try {
          const ra = await adsAPI.listAds(a.id);
          setInlineAds(prev => ({...prev, [a.id]: ra.data}));
        } catch(e) {}
      }));
    } catch(e) { toast.error('Error cargando conjuntos'); }
    setInlineLoading(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '4px' }}>
            Gestiona campañas de pago en Facebook, Instagram y TikTok
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {accounts.length > 0 && (
            <Button variant="secondary" size="sm" onClick={() => setShowCreate(true)} icon="+" >Nueva campaña</Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-elevated)', padding: '4px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', alignSelf: 'flex-start', flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', whiteSpace: 'nowrap',
            background: tab === t.id ? 'var(--bg-card)' : 'transparent',
            color: tab === t.id ? 'var(--text-primary)' : 'var(--text-muted)',
            fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: tab === t.id ? '700' : '500',
            cursor: 'pointer', boxShadow: tab === t.id ? 'var(--shadow-sm)' : 'none',
          }}>{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: '100px', borderRadius: 'var(--radius-lg)' }} />)}
        </div>
      ) : (
        <>
          {/* OVERVIEW */}
          {tab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {accounts.length === 0 ? (
                <Card>
                  <div style={{ textAlign: 'center', padding: '32px 0' }}>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏦</div>
                    <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: '700', marginBottom: '8px' }}>Conecta tu cuenta publicitaria</h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '20px', maxWidth: '400px', margin: '0 auto 20px' }}>
                      Para gestionar campañas de pago, primero sincroniza tu cuenta de Facebook Ads o TikTok for Business.
                    </p>
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Button loading={syncNet === 'facebook'} onClick={() => handleSync('facebook')} icon="🔗">
                        Sincronizar Facebook Ads
                      </Button>
                      <Button variant="secondary" loading={syncNet === 'tiktok'} onClick={() => handleSync('tiktok')} icon="🔗">
                        Sincronizar TikTok Ads
                      </Button>
                      <p style={{fontSize:'12px',color:'var(--text-muted)',marginTop:'8px',textAlign:'center'}}>Facebook Ads incluye Instagram automáticamente.</p>
                    </div>
                  </div>
                </Card>
              ) : (
                <>
                  <OverviewCards data={overview} />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                    <NetworkChart data={overview} />
                    <Card>
                      <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: '700', fontSize: '15px', marginBottom: '16px' }}>Campañas activas</h3>
                      {campaigns.filter(c => c.status === 'ACTIVE').length === 0 ? (
                        <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Sin campañas activas</p>
                      ) : campaigns.filter(c => c.status === 'ACTIVE').slice(0, 4).map(c => (
                        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                          <NetworkIcon network={c.network} size={18} />
                          <span style={{ flex: 1, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                          <span style={{ fontSize: '12px', fontWeight: '700', color: '#10b981' }}>{fmtCurrency(c.spend, c.currency)}</span>
                        </div>
                      ))}
                    </Card>
                  </div>
                </>
              )}
            </div>
          )}

          {/* CAMPAÑAS */}
          {tab === 'campaigns' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {campaigns.length === 0 ? (
                <EmptyState icon="🚀" title="Sin campañas" description="Crea tu primera campaña de pago para empezar a llegar a más personas."
                  action={accounts.length > 0 ? <Button icon="+" onClick={() => setShowCreate(true)}>Crear campaña</Button> : <Button onClick={() => handleSync('facebook')}>Sincronizar cuenta de ads</Button>} />
              ) : campaigns.map(c => (
                <CampaignCard key={c.id} campaign={c} selected={selected?.id === c.id}
                  onStatusChange={handleStatusChange}
                  onDelete={setDeleting}
                  onSelect={camp => { loadInlineAdSets(camp); setTab('adsets'); }}
                  onAdSets={camp => setAdSetsFor(camp)}
                  onEdit={camp => setEditingCampaign(camp)}
                />
              ))}
            </div>
          )}

          {/* CONJUNTOS */}
          {tab === 'adsets' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {/* Selector campaña */}
              <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid var(--border)', marginBottom: '0', overflowX: 'auto' }}>
                {campaigns.map(c => (
                  <button key={c.id} onClick={() => loadInlineAdSets(c)} style={{
                    padding: '8px 16px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap', borderRadius: '6px', marginRight: '6px', marginBottom: '6px',
                    background: selected?.id === c.id ? '#1877F2' : 'var(--bg-elevated)',
                    color: selected?.id === c.id ? '#fff' : 'var(--text-muted)',
                    borderBottom: 'none',
                  }}>{c.name}</button>
                ))}
              </div>
              {!selected ? (
                <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}><div style={{ fontSize: '32px', marginBottom: '8px' }}>🎯</div><p>Selecciona una campaña</p></div>
              ) : inlineLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Cargando...</div>
              ) : (
                <div>
                  {/* Toolbar */}
                  <div style={{ padding: '10px 16px', display: 'flex', gap: '8px', borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
                    <Button size="sm" icon="+" onClick={() => setShowCreateAdSetModal(true)}>Crear conjunto</Button>
                  </div>
                  {/* Tabla */}
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: 'var(--bg-elevated)' }}>
                          <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap' }}>Act.</th>
                          <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap' }}>Conjunto de anuncios</th>
                          <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap' }}>Entrega</th>
                          <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap' }}>Presupuesto</th>
                          <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap' }}>Edad</th>
                          <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap' }}>Impresiones</th>
                          <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap' }}>Clics</th>
                          <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap' }}>Gasto</th>
                          <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap' }}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inlineAdSets.length === 0 ? (
                          <tr><td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Sin conjuntos — <button onClick={() => setShowCreateAdSetModal(true)} style={{ background: 'none', border: 'none', color: '#1877F2', cursor: 'pointer', fontWeight: '600' }}>Crear el primero</button></td></tr>
                        ) : inlineAdSets.map(adset => {
                          const ins = inlineAdSetInsights[adset.id];
                          return (
                            <tr key={adset.id} style={{ borderBottom: '1px solid var(--border)' }} onMouseEnter={e => e.currentTarget.style.background='var(--bg-elevated)'} onMouseLeave={e => e.currentTarget.style.background=''}>
                              <td style={{ padding: '12px 14px' }}>
                                <div onClick={() => { const newStatus = adset.status==='ACTIVE'?'PAUSED':'ACTIVE'; adsAPI.updateAdSetStatus(adset.id, newStatus).then(() => loadInlineAdSets(selected)).catch(() => toast.error('Error')); }} style={{ width: '36px', height: '20px', borderRadius: '10px', background: adset.status==='ACTIVE' ? '#1877F2' : '#ccc', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                                  <div style={{ position: 'absolute', top: '2px', left: adset.status==='ACTIVE' ? '18px' : '2px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                                </div>
                              </td>
                              <td style={{ padding: '12px 14px' }}>
                                <div style={{ fontWeight: '600', fontSize: '13px', color: 'var(--text-primary)' }}>{adset.name}</div>
                                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{adset.optimization_goal || ''}</div><div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>📅 {adset.created_at ? new Date(adset.created_at.replace(' ','T')).toLocaleString('es-CO',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—'}</div>
                              </td>
                              <td style={{ padding: '12px 14px' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: adset.status==='ACTIVE'?'#10b981':'#888', fontWeight: '600' }}>
                                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: adset.status==='ACTIVE'?'#10b981':'#888', display: 'inline-block' }} />
                                  {adset.status==='ACTIVE'?'Activo':'Pausado'}
                                </span>
                              </td>
                              <td style={{ padding: '12px 14px', fontSize: '13px' }}>{fmtCurrency(adset.budget_amount, 'COP')}/día</td>
                              <td style={{ padding: '12px 14px', fontSize: '13px', color: 'var(--text-muted)' }}>{adset.age_min}–{adset.age_max}</td>
                              <td style={{ padding: '12px 14px', textAlign: 'right', fontSize: '13px' }}>{ins ? fmt(ins.impressions||0) : '—'}</td>
                              <td style={{ padding: '12px 14px', textAlign: 'right', fontSize: '13px' }}>{ins ? fmt(ins.clicks||0) : '—'}</td>
                              <td style={{ padding: '12px 14px', textAlign: 'right', fontSize: '13px' }}>{ins ? fmtCurrency(ins.spend||0,'COP') : '—'}</td>
                              <td style={{ padding: '12px 14px' }}>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  <button onClick={() => { setEditingInlineAdSet(adset); }} style={{ padding: '5px 9px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px' }} title="Editar">✏️</button>
                                  <button onClick={() => { if(window.confirm('¿Eliminar este conjunto?')) adsAPI.deleteAdSet(adset.id).then(() => { toast.success('Eliminado'); loadInlineAdSets(selected); }).catch(() => toast.error('Error')); }} style={{ padding: '5px 9px', borderRadius: '6px', border: '1px solid #ef444430', background: '#ef444415', color: '#ef4444', cursor: 'pointer', fontSize: '12px' }} title="Eliminar">🗑</button>

                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ANUNCIOS */}
          {tab === 'ads' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid var(--border)', overflowX: 'auto' }}>
                {campaigns.map(c => (
                  <button key={c.id} onClick={() => loadInlineAdSets(c)} style={{
                    padding: '8px 16px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap', borderRadius: '6px', marginRight: '6px', marginBottom: '6px',
                    background: selected?.id === c.id ? '#1877F2' : 'var(--bg-elevated)',
                    color: selected?.id === c.id ? '#fff' : 'var(--text-muted)',
                  }}>{c.name}</button>
                ))}
              </div>
              {!selected ? (
                <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}><div style={{ fontSize: '32px', marginBottom: '8px' }}>📢</div><p>Selecciona una campaña</p></div>
              ) : inlineLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Cargando...</div>
              ) : (
                <div>
                  <div style={{ padding: '10px 16px', display: 'flex', gap: '8px', borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
                    <Button size="sm" icon="+" onClick={() => { if(inlineAdSets.length === 0){ toast.error('Primero crea un conjunto de anuncios'); return; } if(inlineAdSets.length === 1){ setShowInlineAdForm(inlineAdSets[0].id); } else { setShowInlineAdForm('select'); } }}>Crear anuncio</Button>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: 'var(--bg-elevated)' }}>
                          <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', borderBottom: '2px solid var(--border)' }}>Act.</th>
                          <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', borderBottom: '2px solid var(--border)' }}>Anuncio</th>
                          <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', borderBottom: '2px solid var(--border)' }}>Conjunto</th>
                          <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', borderBottom: '2px solid var(--border)' }}>Entrega</th>
                          <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', borderBottom: '2px solid var(--border)' }}>Impresiones</th>
                          <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', borderBottom: '2px solid var(--border)' }}>Clics</th>
                          <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', borderBottom: '2px solid var(--border)' }}>Gasto</th>
                          <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', borderBottom: '2px solid var(--border)' }}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inlineAdSets.flatMap(adset => (inlineAds[adset.id]||[]).map(ad => {
                          const thumb = ad.thumbnail_url || ad.media_url;
                          return (
                            <tr key={ad.id} style={{ borderBottom: '1px solid var(--border)' }} onMouseEnter={e => e.currentTarget.style.background='var(--bg-elevated)'} onMouseLeave={e => e.currentTarget.style.background=''}>
                              <td style={{ padding: '12px 14px' }}>
                                <div onClick={() => { const s = ad.status==='ACTIVE'?'PAUSED':'ACTIVE'; adsAPI.updateAdStatus(ad.id, s).then(() => loadInlineAdSets(selected)).catch(() => toast.error('Error')); }} style={{ width: '36px', height: '20px', borderRadius: '10px', background: ad.status==='ACTIVE'?'#1877F2':'#ccc', cursor: 'pointer', position: 'relative' }}>
                                  <div style={{ position: 'absolute', top: '2px', left: ad.status==='ACTIVE'?'18px':'2px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                                </div>
                              </td>
                              <td style={{ padding: '12px 14px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  {thumb ? <img src={thumb} alt="" style={{ width: '40px', height: '40px', borderRadius: '6px', objectFit: 'cover' }} onError={e => e.target.style.display='none'} /> : <div style={{ width: '40px', height: '40px', borderRadius: '6px', background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📢</div>}
                                  <div>
                                    <div style={{ fontWeight: '600', fontSize: '13px', color: 'var(--text-primary)' }}>{ad.name}</div>
                                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{ad.media_type==='video'?'🎬 Video':'🖼 Imagen'}</div><div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>📅 {ad.created_at ? new Date(ad.created_at.replace(' ','T')).toLocaleString('es-CO',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—'}</div>
                                  </div>
                                </div>
                              </td>
                              <td style={{ padding: '12px 14px', fontSize: '12px', color: 'var(--text-muted)' }}>{adset.name}</td>
                              <td style={{ padding: '12px 14px' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: ad.status==='ACTIVE'?'#10b981':'#888', fontWeight: '600' }}>
                                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: ad.status==='ACTIVE'?'#10b981':'#888', display: 'inline-block' }} />
                                  {ad.status==='ACTIVE'?'Activo':'Pausado'}
                                </span>
                              </td>
                              <td style={{ padding: '12px 14px', textAlign: 'right', fontSize: '13px' }}>—</td>
                              <td style={{ padding: '12px 14px', textAlign: 'right', fontSize: '13px' }}>—</td>
                              <td style={{ padding: '12px 14px', textAlign: 'right', fontSize: '13px' }}>—</td>
                              <td style={{ padding: '12px 14px' }}>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  <button onClick={() => setEditingInlineAd(ad)} style={{ padding: '5px 9px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px' }} title="Editar">✏️</button>
                                  <button onClick={() => setPreviewInlineAd({...ad, publisher_platforms: adset.publisher_platforms, facebook_positions: adset.facebook_positions, instagram_positions: adset.instagram_positions})} style={{ padding: '5px 9px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px' }} title="Vista previa">👁</button>
                                  <button onClick={() => { if(window.confirm('¿Eliminar este anuncio?')) adsAPI.deleteAd(ad.id).then(() => { toast.success('Anuncio eliminado'); loadInlineAdSets(selected); }).catch(() => toast.error('Error')); }} style={{ padding: '5px 9px', borderRadius: '6px', border: '1px solid #ef444430', background: '#ef444415', color: '#ef4444', cursor: 'pointer', fontSize: '12px' }} title="Eliminar">🗑</button>
                                </div>
                              </td>
                            </tr>
                          );
                        }))}
                        {inlineAdSets.flatMap(a => inlineAds[a.id]||[]).length === 0 && (
                          <tr><td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Sin anuncios en esta campaña</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* MÉTRICAS */}
          {tab === 'insights' && (
            <div style={{ display: 'grid', gridTemplateColumns: campaigns.length > 0 ? '280px 1fr' : '1fr', gap: '16px', alignItems: 'start' }}>
              {campaigns.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <p style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', marginBottom: '4px' }}>SELECCIONA CAMPAÑA</p>
                  {campaigns.map(c => (
                    <div key={c.id} onClick={() => setSelected(c)} style={{
                      padding: '10px 12px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                      background: selected?.id === c.id ? '#7c3aed20' : 'var(--bg-elevated)',
                      border: `1px solid ${selected?.id === c.id ? '#7c3aed' : 'var(--border)'}`,
                      display: 'flex', alignItems: 'center', gap: '8px',
                    }}>
                      <NetworkIcon network={c.network} size={16} />
                      <span style={{ fontSize: '13px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                      <Badge color={STATUS_COLOR[c.status] || '#888'} style={{ fontSize: '10px' }}>{c.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
              <Card>
                <InsightsPanel campaign={selected} />
              </Card>
            </div>
          )}

          {/* CUENTAS AD */}
          {tab === 'accounts' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {(() => {
                  const hasFB = accounts.some(a => a.network === 'facebook');
                  const hasTK = accounts.some(a => a.network === 'tiktok');
                  return (<>
                    <Button loading={syncNet === 'facebook'} onClick={() => handleSync('facebook')} icon="🔗" disabled={hasFB && syncNet !== 'facebook'}>
                      {hasFB ? '✅ Facebook Ads sincronizado' : 'Sincronizar Facebook Ads'}
                    </Button>
                    <Button variant="secondary" loading={syncNet === 'tiktok'} onClick={() => handleSync('tiktok')} icon="🔗" disabled={hasTK && syncNet !== 'tiktok'}>
                      {hasTK ? '✅ TikTok Ads sincronizado' : 'Sincronizar TikTok Ads'}
                    </Button>
                    <p style={{fontSize:'12px',color:'var(--text-muted)',alignSelf:'center'}}>Facebook Ads incluye Instagram automáticamente.</p>
                  </>);
                })()}
              </div>
              {accounts.length === 0 ? (
                <EmptyState icon="🏦" title="Sin cuentas publicitarias" description="Sincroniza tu cuenta de Facebook Ads o TikTok for Business para empezar." />
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
                  {accounts.filter((acc, idx, arr) => arr.findIndex(a => a.account_id === acc.account_id) === idx).map(acc => (
                    <Card key={acc.id} style={{ borderLeft: `3px solid ${NET_COLOR[acc.network] || '#888'}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                        <NetworkIcon network={acc.network} size={24} />
                        <div>
                          <div style={{ fontWeight: '700', fontSize: '14px' }}>{acc.name}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>ID: {acc.account_id}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <Badge color="#10b981">{acc.currency}</Badge>
                        {acc.timezone && <Badge color="#555">{acc.timezone}</Badge>}
                        <Badge color={acc.status === 'active' ? '#10b981' : '#ef4444'}>{acc.status}</Badge>
                      </div>
                      <Button size="sm" variant="secondary" style={{ marginTop: '12px' }}
                        onClick={() => adsAPI.syncFromPlatform(acc.id).then(() => { toast.success('Campañas importadas'); load(); }).catch(e => toast.error(e?.response?.data?.detail || 'Error'))}>
                        Importar campañas existentes
                      </Button>

                      <div style={{ marginTop: '12px', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>🎯 Meta Pixel ID</label>
                        {acc.pixel_id
                          ? <div style={{fontSize:'11px',color:'#10b981',marginBottom:'6px'}}>✅ Pixel configurado — campañas de conversión habilitadas</div>
                          : <div style={{fontSize:'11px',color:'#f59e0b',marginBottom:'6px'}}>⚠️ Sin pixel — objetivos de Conversión y Ventas no disponibles</div>
                        }
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <input defaultValue={acc.pixel_id || ''} placeholder="Ej: 1234567890123456" id={`pixel-${acc.id}`} style={{ flex: 1, padding: '7px 10px', borderRadius: '7px', background: 'var(--bg-elevated)', border: `1px solid ${acc.pixel_id ? '#10b981' : 'var(--border)'}`, color: '#fff', fontSize: '12px', outline: 'none' }} />
                          <button onClick={async () => { const val = document.getElementById(`pixel-${acc.id}`).value.trim(); try { const { default: api } = await import('../api/client'); await api.patch(`/ads/accounts/${acc.id}/pixel`, { pixel_id: val }); toast.success(val ? 'Pixel guardado ✓' : 'Pixel eliminado'); load(); } catch(e) { toast.error('Error guardando pixel'); } }} style={{ padding: '7px 12px', borderRadius: '7px', border: 'none', background: '#7c3aed', color: '#fff', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>Guardar</button>
                        </div>
                        <div style={{fontSize:'11px',color:'var(--text-muted)',marginTop:'6px'}}>💡 Obtén tu Pixel ID en <a href="https://business.facebook.com/events_manager" target="_blank" rel="noreferrer" style={{color:'#a78bfa'}}>Meta Events Manager</a></div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Modal crear campaña */}
      {showCreate && accounts.length > 0 && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 299, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={(e) => { if(e.target===e.currentTarget) setShowCreate(false); }}>
          <div className="animate-scaleIn" style={{ width: 'min(600px, 95vw)', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: '24px', maxHeight: '85vh', overflowY: 'auto', overflowX: 'hidden' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: '800', fontSize: '18px', marginBottom: '20px' }}>Nueva campaña de pago</h2>
            <CreateCampaignForm accounts={accounts} onSave={handleCreate} onCancel={() => setShowCreate(false)} saving={saving} />
          </div>
        </div>
      )}

      {adSetsFor && accounts.length > 0 && (
        <AdSetsModal campaign={adSetsFor} adAccountId={accounts[0]?.id} onClose={() => setAdSetsFor(null)} autoCreateAdSet={true} />
      )}
      {previewInlineAd && <AdPreviewsModal ad={previewInlineAd} onClose={() => setPreviewInlineAd(null)} onEdit={(ad) => { setPreviewInlineAd(null); setEditingInlineAd(ad); }} />}
      {showInlineAdForm && showInlineAdForm !== 'select' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 399, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={e => { if(e.target===e.currentTarget) setShowInlineAdForm(null); }}>
          <div style={{ width: 'min(700px, 96vw)', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: '24px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: '700', fontSize: '15px', marginBottom: '16px' }}>+ Nuevo anuncio</h3>
            <CreateAdForm adSetId={showInlineAdForm} adAccountId={accounts[0]?.id} onSave={async (form) => { setSaving(true); try { await adsAPI.createAd(form); toast.success('Anuncio creado'); setShowInlineAdForm(null); await loadInlineAdSets(selected); } catch(e) { toast.error(e?.response?.data?.detail || 'Error'); } finally { setSaving(false); } }} onCancel={() => setShowInlineAdForm(null)} saving={saving} campaign={selected} adset={inlineAdSets.find(a => a.id === showInlineAdForm)} pixelId={accounts[0]?.pixel_id || null} />
          </div>
        </div>
      )}
      {showInlineAdForm === 'select' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)', zIndex: 399, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={e => { if(e.target===e.currentTarget) setShowInlineAdForm(null); }}>
          <div style={{ width: 'min(440px, 96vw)', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '28px', boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: '800', fontSize: '17px', color: 'var(--text-primary)' }}>📦 Selecciona un conjunto</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>El anuncio se creará dentro del conjunto elegido</div>
              </div>
              <button onClick={() => setShowInlineAdForm(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '20px', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {inlineAdSets.map(a => (
                <button key={a.id} onClick={() => setShowInlineAdForm(a.id)} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', padding: '12px 16px', borderRadius: '10px',
                  border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                  cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                  fontFamily: 'var(--font-body)',
                }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>{a.name}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{a.status} · {a.budget_type === 'DAILY' ? 'Diario' : 'Total'}</div>
                  </div>
                  <span style={{ color: '#7c3aed', fontSize: '18px' }}>›</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {editingInlineAd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 399, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={e => { if(e.target===e.currentTarget) setEditingInlineAd(null); }}>
          <div style={{ width: 'min(700px, 96vw)', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: '24px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: '700', fontSize: '15px', marginBottom: '16px' }}>✏️ Editar anuncio</h3>
            <EditAdForm ad={editingInlineAd} adAccountId={accounts[0]?.id} onSave={(form) => handleInlineAdEdit(editingInlineAd.id, form)} onCancel={() => setEditingInlineAd(null)} saving={inlineSaving} />
          </div>
        </div>
      )}
      {showCreateAdSetModal && selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 399, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={e => { if(e.target===e.currentTarget) setShowCreateAdSetModal(false); }}>
          <div style={{ width: 'min(700px, 96vw)', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: '24px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: '700', fontSize: '15px', marginBottom: '16px' }}>Nuevo conjunto de anuncios</h3>
            <CreateAdSetForm campaignId={selected.id} adAccountId={accounts[0]?.id} budgetLevel={selected.budget_level || "CAMPAIGN"} onSave={async (form) => { try { const newAdSet = await adsAPI.createAdSet(selected.id, form); toast.success('Conjunto creado ✓'); setShowCreateAdSetModal(false); await loadInlineAdSets(selected); } catch(e) { toast.error(e?.response?.data?.detail || 'Error'); } }} onCancel={() => setShowCreateAdSetModal(false)} saving={false} campaign={selected} />
          </div>
        </div>
      )}
      {editingCampaign && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 399, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={e => { if(e.target===e.currentTarget) setEditingCampaign(null); }}>
          <div style={{ width: 'min(700px, 96vw)', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: '24px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: '700', fontSize: '15px', marginBottom: '16px' }}>✏️ Editar campaña</h3>
            <EditCampaignForm campaign={editingCampaign} accounts={accounts} onSave={handleCampaignEdit} onCancel={() => setEditingCampaign(null)} saving={savingCampaign} />
          </div>
        </div>
      )}
      {editingInlineAdSet && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 399, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={e => { if(e.target===e.currentTarget) setEditingInlineAdSet(null); }}>
          <div style={{ width: 'min(700px, 96vw)', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: '24px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: '700', fontSize: '15px', marginBottom: '16px' }}>✏️ Editar conjunto</h3>
            <EditAdSetForm adset={editingInlineAdSet} onSave={(form) => handleInlineAdSetEdit(editingInlineAdSet.id, form)} onCancel={() => setEditingInlineAdSet(null)} saving={false} />
          </div>
        </div>
      )}

      <ConfirmDialog open={!!deleting} onClose={() => setDeleting(null)} onConfirm={() => handleDelete(deleting)}
        title="Eliminar campaña" message={`¿Eliminar "${deleting?.name}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar" danger />
    </div>
  );
}
